import { Label } from '@ui/Label';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import { useTheme } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import { makeStyles } from 'tss-react/mui';
import { useAuth0 } from '@auth0/auth0-react';
import { io, Socket } from 'socket.io-client';
import * as uuid from 'uuid';
import { useUser } from '@client/hooks/use-user.hooks';
import { useSearchParam } from '@client/hooks/use-search-param.hook';
import { isAdmin } from '@shared/helpers/user.helpers';
import {
  type ProjectFileBatchBuildingComposition,
  type ProjectFileBatchJoinedBlueprintExtraction,
  ProjectFileBatchAdminRow,
  ProjectFileBatchSnapshot,
  ProjectFileBatchState,
  ProjectFileBatchTimelineEntry,
  type ProjectFileState,
  type BlueprintSubtype,
  ProjectFileClassificationEnvelope,
  PROJECT_FILE_DOCUMENT_KIND_AI_GUIDE,
  isTerminalBatchState,
  projectFileBatchAggregatedOutputSchema,
  projectFileBatchBuildingCompositionSchema,
  projectFileBatchJoinedBlueprintExtractionSchema,
} from '@shared/models/project-file-batch.model';
import { SocketEventName } from '@shared/helpers/socket.helpers';
import {
  type ProjectFileTimelineSpan,
  buildTimelineSpans,
  computePipelineTimelineExtents,
  formatPhaseDurationMs,
  getProjectFilePhaseDurationMs,
  layoutTimelineSegments,
} from '@shared/helpers/project-file-batch-timeline.helpers';
import { getSuggestedProjectLabelsFromProjectFiles } from '@shared/helpers/project-file-extraction-suggestion.helpers';
import { tryCatchAwaitable } from '@shared/helpers/utils.helpers';
import {
  getProjectFileBatchAiChainPromptSections,
  type ProjectFileBatchAiChainStructuredOutput,
} from '@shared/helpers/project-file-batch-ai-chain-prompt-catalog.helpers';
import { humanizeJsonSchema } from '../AdminAIChatTools/humanize-schema.helpers';
import {
  isProjectFileExtractionOutputV1,
  type ProjectFileExtractionOutputV1,
} from '@shared/models/project-file-extraction.model';
import {
  buildingPartRequirementAggregatedSchema,
  type BuildingPartRequirement,
  type BuildingPartRequirementAggregated,
} from '@shared/models/building-part-requirement.model';
import {
  extractedDesignFactConflictSchema,
  type ExtractedDesignFactConflict,
} from '@shared/models/extracted-design-facts.model';
import {
  ffuOutputFactsSchema,
  type FfuBuilding,
  type FfuOutputFacts,
} from '@shared/models/ffu-output.model';
import { formatStoreyCount } from '@shared/helpers/storey-level.helpers';
import {
  extractedDesignFactPathLabel,
  formatExtractedDesignFactConflictNotice,
  extractedBuildingDisplayGroupKey,
  formatFfuRoof,
} from '@shared/helpers/extracted-design-facts.helpers';
import {
  buildingPartRequirementCards,
  formatBuildingPartConstraint,
  formatBuildingPartTarget,
  formatStoredRequirementScopeCaption,
  isElementRequirement,
  formatRequirementCardHeading,
  mergeBuildingPartRequirementRows,
  parseBuildingPartRequirement,
  requirementCardFields,
} from '@shared/helpers/building-part-requirement.helpers';
import { readFfuBatchOutput } from '@shared/helpers/ffu-output-view.helpers';
import { OPEN_AI_ALLOWED_MODELS } from '@shared/models/ai/ai-models.model';
import {
  FFU_PIPE_ID,
  FFU_SINGLE_CALL_PIPE_ID,
  PROJECT_FILE_PIPES,
  isProjectFilePipeId,
  isSingleCallProjectFilePipe,
  projectFilePipeLabel,
  type ProjectFilePipeId,
} from '@shared/models/project-file-pipe.model';
import { useConfig } from '../../providers/ConfigProvider';
import { useSelectedOrganizationName } from '@client/store/organization';
import clsx from 'clsx';
import {
  collectProjectFilesFromDataTransfer,
  collectProjectFilesFromFileList,
  type ProjectFileClientUpload,
} from '../../helpers/project-file-upload-normalize.helpers';
import { getErrorResponseMessage } from '@client/hooks/use-error-handler.hooks';
import { adminProjectFileBatchesApi } from './admin-project-file-batches.api';
import { FfuOutputView } from './FfuOutputView';
import { BatchViewErrorBoundary } from './BatchViewErrorBoundary';
import { ExtractedDrawingsDialog } from './ExtractedDrawingsDialog';
import { formatFileSizeMegabytes } from './file-size.helpers';
import {
  aiStepCostRow,
  formatAiCostSummary,
  formatAiCostUsd,
} from './ai-cost.helpers';
import {
  summarizeProjectFileAiCost,
  summarizeProjectFileAiCostByStep,
} from '@shared/helpers/project-file-pipeline-ai.helpers';
import type { ProjectFileAiCall } from '@shared/models/project-file-ai-call.model';
import { storeyGroups, storeyTable } from './storey-table.helpers';
import {
  buildingStoreyRows,
  buildStoreyPreviews,
  soleStoreySectionIndex,
  type BuildingStoreyRow,
} from './plan-storey-previews.helpers';
import {
  PlanOverlayStack,
  PlanOverlayStages,
  planOverlayGroups,
  type PlanOverlayGroup,
} from './PlanOverlayStack';

/** Resolved profile label; never shows raw Auth0 subject ids (use em dash when missing). */
const visibleUploaderName = (label: string | null | undefined): string => {
  const t = label?.trim();
  return t ? t : '—';
};

/** Label column beside pipeline lanes + matching caption spacer; keep in sync. */
const TIMELINE_LANE_LABEL_PX = 200;

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
    backgroundColor: theme.palette.background.default,
  },
  mainRow: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
    overflow: 'hidden',
  },
  list: {
    width: 260,
    flexShrink: 0,
    borderRight: `1px solid ${theme.palette.divider}`,
    overflow: 'auto',
  },
  detail: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.mode === 'dark' ? '#121212' : '#fafafa',
  },
  detailContent: {
    width: '100%',
    maxWidth: '72rem',
    minWidth: 0,
    margin: '0 auto',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    color: theme.palette.text.secondary,
    gap: theme.spacing(2),
    padding: theme.spacing(4),
    textAlign: 'center',
  },
  emptyStateIcon: {
    fontSize: 48,
    opacity: 0.5,
  },
  detailIdlePromptLayout: {
    display: 'flex',
    flexDirection: 'column',
    maxWidth: 1000,
    margin: '0 auto',
    paddingBottom: theme.spacing(8),
  },
  batchAiPromptChainScroll: {
    marginTop: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(4),
  },
  batchAiPromptCard: {
    overflow: 'hidden',
    borderLeft: `4px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.shadows[1],
  },
  batchAiPromptCardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    '&:last-child': { paddingBottom: theme.spacing(3) },
  },
  batchAiPromptIntro: {
    marginTop: theme.spacing(2),
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '1.1rem',
  },
  batchAiPromptCaption: {
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
    lineHeight: 1.6,
  },
  batchAiPromptMono: {
    fontFamily: 'monospace',
    fontSize: '0.85rem',
    backgroundColor: theme.palette.action.hover,
    padding: '2px 4px',
    borderRadius: 4,
    color: theme.palette.primary.dark,
  },
  batchAiPromptPurpose: {
    color: theme.palette.text.primary,
    lineHeight: 1.6,
    fontSize: '0.95rem',
  },
  batchAiPromptContextHeading: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  batchAiPromptContextList: {
    margin: 0,
    paddingLeft: theme.spacing(2),
    marginBottom: 0,
  },
  batchAiPromptContextLi: {
    marginBottom: theme.spacing(0.5),
    lineHeight: 1.5,
    '&:last-child': {
      marginBottom: 0,
    },
  },
  batchAiPromptBodyHeading: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  batchAiPromptStructuredHeading: {
    fontWeight: theme.typography.fontWeightBold,
    fontSize: '0.8rem',
    textTransform: 'uppercase',
    letterSpacing: 1,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  batchAiPromptStructuredSlice: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(2),
    paddingTop: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  batchAiPromptStructuredSliceFirst: {
    marginTop: 0,
    paddingTop: 0,
    borderTop: 'none',
  },
  batchAiPromptStructuredSliceToolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    justifyContent: 'space-between',
  },
  batchAiPromptSchemaLabel: {
    fontWeight: theme.typography.fontWeightMedium,
    color: theme.palette.text.primary,
  },
  batchAiPromptSchemaNameChip: {
    fontFamily: 'monospace',
    fontSize: '0.75rem',
    maxWidth: '100%',
    height: 'auto',
    backgroundColor: theme.palette.action.selected,
    '& .MuiChip-label': {
      whiteSpace: 'normal',
      textAlign: 'left',
      display: 'block',
      lineHeight: 1.3,
      padding: '4px 8px',
    },
  },
  batchAiPromptSchemaMonoPre: {
    margin: 0,
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    padding: theme.spacing(2),
    background: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : '#f8f9fa',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'visible',
  },
  batchAiPromptPhase: {
    display: 'inline-block',
    marginBottom: theme.spacing(1),
    fontSize: '0.7rem',
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
    letterSpacing: 1.2,
    textTransform: 'uppercase',
    backgroundColor: theme.palette.primary.light,
    padding: '2px 8px',
    borderRadius: 4,
    opacity: 0.8,
  },
  row: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    minWidth: 0,
    padding: theme.spacing(0.75, 1.25),
    borderBottom: `1px solid ${theme.palette.divider}`,
    cursor: 'pointer',
    '&:hover': { backgroundColor: theme.palette.action.hover },
  },
  rowName: {
    margin: 0,
    overflow: 'hidden',
    fontSize: theme.typography.body2.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.3,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowMeta: {
    margin: 0,
    overflow: 'hidden',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    lineHeight: 1.3,
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  rowActive: {
    backgroundColor: theme.palette.action.selected,
  },
  suggestedFromExtraction: {
    display: 'flex',
    flexDirection: 'column',
    gap: 3,
    minWidth: 0,
  },
  meta: {
    fontSize: 12,
    color: theme.palette.text.secondary,
    display: 'flex',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    marginTop: 4,
  },
  pre: {
    margin: 0,
    fontFamily: 'monospace',
    fontSize: 12,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    padding: theme.spacing(2),
    background: theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.2)' : '#f8f9fa',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'visible',
  },
  section: {
    marginTop: theme.spacing(2),
  },
  consolidatedResultCard: {
    marginTop: theme.spacing(1),
  },
  consolidatedOutputSkeletonRoot: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
  },
  filePendingBlock: {
    marginTop: theme.spacing(0.75),
  },
  fileDrawingsButton: {
    marginTop: theme.spacing(0.5),
  },
  fileClassificationSkeletonCards: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  filePendingDocumentKindRow: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.25),
  },
  extractionSkeletonBox: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  classificationGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    gridTemplateColumns: '1fr',
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    },
  },
  classificationFallback: {
    gridColumn: '1 / -1',
  },
  classificationCard: {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    borderLeftWidth: 3,
    borderLeftStyle: 'solid',
    borderLeftColor: theme.palette.primary.main,
    padding: theme.spacing(1.5, 2),
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.04)'
        : theme.palette.grey[50],
    minWidth: 0,
  },
  classificationChipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    alignItems: 'center',
  },
  classificationMeta: {
    display: 'block',
    marginTop: theme.spacing(0.75),
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.caption.fontSize,
    lineHeight: theme.typography.caption.lineHeight,
    color: theme.palette.text.secondary,
  },
  classificationDetailLine: {
    marginTop: theme.spacing(0.75),
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight,
  },
  classificationDetailLabel: {
    fontWeight: theme.typography.fontWeightMedium,
    marginRight: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  dropZone: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(1),
    margin: theme.spacing(1),
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'background-color 0.15s, border-color 0.15s',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  dropZoneActive: {
    backgroundColor: theme.palette.action.selected,
    borderColor: theme.palette.primary.main,
  },
  jsonScrollViewport: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.03)'
        : theme.palette.grey[100],
    maxHeight: 420,
    overflow: 'auto',
  },
  jsonPreInViewport: {
    margin: 0,
    fontFamily: 'monospace',
    fontSize: 11,
    lineHeight: 1.45,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  aggregatedLead: {
    marginTop: theme.spacing(1),
  },
  aggregatedChipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    alignItems: 'center',
    marginTop: theme.spacing(0.75),
  },
  consolidatedOutputStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
    maxWidth: '100%',
  },
  consolidatedSectionTitle: {
    margin: 0,
    marginBottom: theme.spacing(1.25),
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.subtitle1.fontSize,
    lineHeight: theme.typography.subtitle1.lineHeight,
  },
  consolidatedSectionSubhead: {
    marginBottom: theme.spacing(0.5),
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.subtitle2.fontSize,
    color: theme.palette.text.secondary,
  },
  consolidatedFieldBlock: {
    marginTop: theme.spacing(1.5),
    '&:first-of-type': { marginTop: 0 },
  },
  consolidatedFieldCaption: {
    display: 'block',
    marginBottom: theme.spacing(0.5),
    fontSize: theme.typography.caption.fontSize,
    lineHeight: theme.typography.caption.lineHeight,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  consolidatedFieldBody: {
    margin: 0,
    fontSize: theme.typography.body1.fontSize,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  sourceFilesListRoot: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    gap: theme.spacing(1),
    width: '100%',
    maxHeight: 320,
    overflow: 'auto',
    paddingBottom: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
  },
  sourceFileListEntry: {
    flex: '0 1 auto',
    alignSelf: 'flex-start',
    width: 'fit-content',
    maxWidth: '100%',
    boxSizing: 'border-box',
    padding: theme.spacing(1.25),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.04)'
        : theme.palette.grey[50],
  },
  sourceFileListPrimary: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.body1.fontSize,
    lineHeight: 1.4,
    wordBreak: 'break-word',
  },
  sourceFileListIdLine: {
    fontFamily:
      '"SF Mono","Roboto Mono","Consolas","Liberation Mono",monospace',
    fontSize: 10,
    lineHeight: 1.35,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(0.5),
    wordBreak: 'break-all',
  },
  consolidatedBuildingCard: {
    marginTop: 0,
    padding: theme.spacing(1.25, 1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.03)'
        : theme.palette.grey[50],
    minWidth: 0,
  },
  consolidatedBuildingHeadingRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    columnGap: theme.spacing(1),
    rowGap: theme.spacing(0.25),
    marginBottom: theme.spacing(1),
  },
  consolidatedBuildingName: {
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.subtitle2.fontSize,
    lineHeight: 1.35,
    wordBreak: 'break-word',
  },
  consolidatedBuildingHeadingSep: {
    color: theme.palette.text.secondary,
    userSelect: 'none',
  },
  consolidatedBuildingInstanceKey: {
    fontFamily:
      '"SF Mono","Roboto Mono","Consolas","Liberation Mono",monospace',
    fontSize: 11,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
    wordBreak: 'break-all',
  },
  consolidatedBuildingBlock: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    minWidth: 0,
  },
  consolidatedBuildingsGrid: {
    display: 'grid',
    width: '100%',
    gap: theme.spacing(2),
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    [theme.breakpoints.down('lg')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
    },
  },
  designFactsSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  designFactsDl: {
    display: 'grid',
    gridTemplateColumns: 'max-content max-content',
    justifyContent: 'start',
    columnGap: theme.spacing(1),
    rowGap: theme.spacing(0.125),
    margin: 0,
  },
  designFactDt: {
    margin: 0,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.35,
  },
  designFactDd: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'baseline',
    gap: theme.spacing(0.75),
    margin: 0,
    minWidth: 0,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.35,
    wordBreak: 'break-word',
  },
  designFactAlso: {
    fontSize: theme.typography.caption.fontSize,
    lineHeight: 1.35,
    color: theme.palette.text.secondary,
  },
  designFactsBuildingsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    width: '100%',
  },
  designFactsCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    minWidth: 0,
  },
  designFactsBuildingName: {
    margin: 0,
    fontWeight: theme.typography.fontWeightMedium,
    fontSize: theme.typography.body1.fontSize,
    lineHeight: 1.3,
    wordBreak: 'break-word',
  },
  designFactsStoreys: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.125),
    margin: 0,
    padding: 0,
    listStyle: 'none',
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.35,
  },
  outputRoot: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    width: '100%',
    minWidth: 0,
  },
  outputHeader: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    maxWidth: '68ch',
  },
  outputTitle: {
    margin: 0,
    fontSize: theme.typography.h5.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.25,
  },
  outputLocation: {
    margin: 0,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.4,
  },
  outputLead: {
    display: '-webkit-box',
    overflow: 'hidden',
    margin: 0,
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 2,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.45,
  },
  outputSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    minWidth: 0,
  },
  outputBuildingStack: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    minWidth: 0,
  },
  outputBuildingRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    minWidth: 0,
  },
  outputBuildingFacts: {
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    minWidth: 0,
    overflowX: 'auto',
  },
  /** At least 30 % of the row so the plan stays readable next to a wide table. */
  outputBuildingPlans: {
    flex: '0 0 30%',
    alignSelf: 'flex-start',
    minWidth: '30%',
    maxWidth: '50%',
  },
  outputSectionTitle: {
    margin: 0,
    fontSize: theme.typography.subtitle1.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.3,
  },
  outputCaption: {
    margin: 0,
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightMedium,
    letterSpacing: '0.04em',
    lineHeight: 1.3,
    textTransform: 'uppercase',
  },
  outputMetricRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  outputMetric: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: theme.spacing(0.25),
    minWidth: 96,
    padding: theme.spacing(1.25, 1.5),
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  outputMetricValue: {
    fontSize: '1.25rem',
    fontWeight: theme.typography.fontWeightMedium,
    fontVariantNumeric: 'tabular-nums',
    lineHeight: 1.2,
    wordBreak: 'break-word',
  },
  outputMetricLabel: {
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    letterSpacing: '0.04em',
    lineHeight: 1.3,
    textTransform: 'uppercase',
  },
  outputRequirementCard: {
    maxWidth: '16rem',
  },
  outputRequirementField: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.25),
  },
  outputMetricMeta: {
    display: 'block',
    marginTop: theme.spacing(0.25),
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightRegular,
    letterSpacing: 0,
    lineHeight: 1.3,
    textTransform: 'none',
    wordBreak: 'break-word',
  },
  outputTableWrap: {
    width: '100%',
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'auto',
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  outputTable: {
    width: 'max-content',
    minWidth: '100%',
    borderCollapse: 'collapse',
    fontSize: theme.typography.body2.fontSize,
    '& th, & td': {
      padding: theme.spacing(1, 1.5),
      borderBottom: `1px solid ${theme.palette.divider}`,
      verticalAlign: 'top',
      whiteSpace: 'nowrap',
    },
    '& th': {
      verticalAlign: 'bottom',
      color: theme.palette.text.secondary,
      fontSize: theme.typography.caption.fontSize,
      fontWeight: theme.typography.fontWeightMedium,
      letterSpacing: '0.04em',
      textAlign: 'left',
      textTransform: 'uppercase',
    },
    '& tbody tr:last-child td': {
      borderBottom: 'none',
    },
  },
  outputCellRight: {
    fontVariantNumeric: 'tabular-nums',
    textAlign: 'right',
    whiteSpace: 'nowrap',
    'th&': {
      textAlign: 'right',
    },
  },
  outputKey: {
    marginLeft: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: theme.typography.caption.fontSize,
    fontWeight: theme.typography.fontWeightRegular,
  },
  outputStoreyGround: {
    backgroundColor: theme.palette.action.hover,
  },
  outputConflictList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    margin: 0,
    padding: 0,
    listStyle: 'none',
    color: theme.palette.text.secondary,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.4,
  },
  outputWarn: {
    margin: 0,
    color: theme.palette.warning.main,
    fontSize: theme.typography.caption.fontSize,
  },
  batchFilesFlex: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.spacing(1.5),
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    marginTop: theme.spacing(1),
    width: '100%',
  },
  batchFileTile: {
    flex: '0 1 auto',
    alignSelf: 'flex-start',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    width: 'fit-content',
    maxWidth: '100%',
    minWidth: 0,
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.03)'
        : theme.palette.grey[50],
    gap: theme.spacing(0.25),
  },
  batchFileHeaderRow: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    columnGap: theme.spacing(2),
    minWidth: 0,
  },
  batchFilePrimary: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(1),
    flex: '0 1 auto',
    minWidth: 0,
  },
  batchFileMetaCaption: {
    flex: '0 1 auto',
    alignSelf: 'center',
    minWidth: 0,
  },
  consolidatedDescription: {
    margin: 0,
    marginTop: theme.spacing(0.75),
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  extractionSegmentsGrid: {
    display: 'grid',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    gridTemplateColumns: '1fr',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  segmentPairsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    marginTop: theme.spacing(0.75),
  },
  segmentPairRow: {
    display: 'grid',
    gap: theme.spacing(1.25),
    gridTemplateColumns: '1fr',
    gridTemplateAreas: '"header" "left" "right"',
    padding: theme.spacing(1, 1.25),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.02)'
        : 'rgba(0,0,0,0.02)',
    [theme.breakpoints.up('md')]: {
      gridTemplateColumns: '1fr 1fr',
      gridTemplateAreas: '"header header" "left right"',
    },
  },
  segmentPairHeader: {
    gridArea: 'header',
    display: 'flex',
    gap: theme.spacing(0.5),
    alignItems: 'center',
  },
  segmentPairCell: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    '&:nth-of-type(2)': { gridArea: 'left' },
    '&:nth-of-type(3)': { gridArea: 'right' },
  },
  segmentPairEmptyCell: {
    minWidth: 0,
    padding: theme.spacing(1, 1.25),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    color: theme.palette.text.secondary,
    fontStyle: 'italic',
  },
  extractionSegmentCard: {
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`,
    borderLeftWidth: 3,
    borderLeftStyle: 'solid',
    padding: theme.spacing(1.25, 1.5),
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(255,255,255,0.04)'
        : theme.palette.grey[50],
    minWidth: 0,
  },
  extractionSegmentCardOk: {
    borderLeftColor: theme.palette.success.main,
  },
  extractionSegmentCardFail: {
    borderLeftColor: theme.palette.error.main,
  },
  extractionChipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    alignItems: 'center',
  },
  extractionMeta: {
    display: 'block',
    marginTop: theme.spacing(0.75),
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.caption.fontSize,
    lineHeight: theme.typography.caption.lineHeight,
    color: theme.palette.text.secondary,
  },
  extractionErrorLine: {
    display: 'block',
    marginTop: theme.spacing(1),
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  timelineTrack: {
    position: 'relative',
    width: '100%',
    minHeight: 36,
    marginTop: 0,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(0,0,0,0.35)'
        : theme.palette.grey[200],
  },
  timelineSegment: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingLeft: theme.spacing(0.25),
    paddingRight: theme.spacing(0.25),
    overflow: 'hidden',
    // No transition on left/width — wall clock ticks every frame; eased motion fights updates.
    transition: theme.transitions.create(['background-color'], {
      duration: theme.transitions.duration.shorter,
    }),
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  timelineSegmentLive: {
    animation: '$timelineLivePulse 2.4s ease-in-out infinite',
    '@media (prefers-reduced-motion: reduce)': {
      animation: 'none',
    },
  },
  timelineCaption: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    flexWrap: 'nowrap',
    gap: theme.spacing(1),
    flex: 1,
    minWidth: 0,
    fontSize: 11,
    lineHeight: 1.35,
    color: theme.palette.text.secondary,
    fontVariantNumeric: 'tabular-nums',
    transition: 'opacity 0.35s ease',
  },
  timelineUnifiedStack: {
    marginTop: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
  timelineUnifiedRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    minWidth: 0,
  },
  timelineRowLabelCol: {
    flexShrink: 0,
    width: TIMELINE_LANE_LABEL_PX,
    minHeight: 0,
    overflow: 'hidden',
    fontSize: 11,
    lineHeight: 1.25,
    color: theme.palette.text.secondary,
    fontWeight: theme.typography.fontWeightMedium,
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    display: 'block',
    boxSizing: 'border-box',
    paddingRight: theme.spacing(0.5),
  },
  timelineCaptionSpacerCell: {
    flexShrink: 0,
    width: TIMELINE_LANE_LABEL_PX,
    minWidth: TIMELINE_LANE_LABEL_PX,
    flexBasis: TIMELINE_LANE_LABEL_PX,
  },
  timelineRowTrackWrap: {
    flex: 1,
    minWidth: 0,
  },
  timelineLaneStacked: {
    width: '100%',
    marginTop: 0,
  },
  timelinePlayhead: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 2,
    marginLeft: -1,
    zIndex: 2,
    borderRadius: 1,
    backgroundColor: theme.palette.common.white,
    boxShadow:
      theme.palette.mode === 'dark'
        ? '0 0 6px rgba(255,255,255,0.45)'
        : '0 0 5px rgba(0,0,0,0.35)',
    pointerEvents: 'none',
    transition: 'left 0.12s linear',
    '@media (prefers-reduced-motion: reduce)': {
      transition: 'none',
    },
  },
  timelineSegmentLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    textAlign: 'center',
    fontSize: 10,
    lineHeight: 1.2,
    maxWidth: '100%',
    fontFamily: theme.typography.fontFamily,
  },
  timelineRawWrap: {
    marginTop: theme.spacing(1),
  },
  timelineRawSummary: {
    cursor: 'pointer',
    userSelect: 'none',
    fontSize: theme.typography.caption.fontSize,
    color: theme.palette.text.secondary,
  },
  '@keyframes timelineLivePulse': {
    '0%, 100%': { opacity: 1 },
    '50%': { opacity: 0.82 },
  },
}));

const STATE_COLOR: Record<
  ProjectFileBatchState,
  'default' | 'primary' | 'warning' | 'success' | 'error'
> = {
  collecting: 'default',
  classifying: 'primary',
  awaiting_confirmation: 'warning',
  extracting: 'primary',
  consolidating: 'primary',
  done: 'success',
  interrupted: 'warning',
  failed: 'error',
};

const formatTime = (iso: string): string => new Date(iso).toLocaleString();

/** Turn enum-style keys (e.g. `blueprint_floor`) into readable words. */
const humanizeFlatKey = (s: string): string =>
  s
    .trim()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');

/** Stored classifier value. Anything else is not printed. */
const storedBlueprintSubtype = (
  value: string | null | undefined,
): BlueprintSubtype | null => {
  if (value === 'side_view' || value === 'top_view') return value;
  return null;
};

const classificationTypeLabel = (
  type: string,
  blueprintSubtype: string | null | undefined,
): string => {
  const typeLabel = humanizeFlatKey(type);
  const subtype = storedBlueprintSubtype(blueprintSubtype);
  if (subtype == null) return typeLabel;
  return `${typeLabel} · ${subtype}`;
};

/** Leftover keys the blueprint schema no longer asks the model to fill. */
const isDroppedBlueprintExtractionKey = (key: string): boolean =>
  key === 'plan_shape' || key === 'break_at';

/** Short labels for pipeline states on chips and bars. */
const friendlyBatchPhase = (state: ProjectFileBatchState): string => {
  const m: Record<ProjectFileBatchState, string> = {
    collecting: 'Receiving uploads',
    classifying: 'Classifying',
    awaiting_confirmation: 'Waiting',
    extracting: 'Reading files',
    consolidating: 'Building summary',
    done: 'Finished',
    interrupted: 'Paused',
    failed: 'Failed',
  };
  return m[state] ?? humanizeFlatKey(state);
};

const friendlyFilePhase = (state: ProjectFileState): string => {
  const m: Record<ProjectFileState, string> = {
    queued: 'Waiting',
    preprocessing: 'Preparing',
    classifying: 'Classifying',
    classified: 'Typed',
    extracting: 'Reading details',
    done: 'Done',
    failed: 'Failed',
    cancelled: 'Cancelled',
  };
  return m[state] ?? humanizeFlatKey(state);
};

const friendlyLaneState = (lane: 'batch' | 'file', raw: string): string =>
  lane === 'batch'
    ? friendlyBatchPhase(raw as ProjectFileBatchState)
    : friendlyFilePhase(raw as ProjectFileState);

/** Live-bar tick while a batch is in flight. ~30 fps stays smooth; 60 React commits/sec + MUI Tooltip was killing the tab. */
const TIMELINE_LIVE_TICK_MS = 32;

/** Advances `Date.now()` while `enabled`; keeps open spans lengthening visually. */
const useWallClockMs = (enabled: boolean): number => {
  const [ms, setMs] = useState(() => Date.now());

  useEffect(() => {
    if (!enabled) return;
    let frameId = 0;
    let lastCommit = 0;
    const tick = (frameTime: number) => {
      if (frameTime - lastCommit >= TIMELINE_LIVE_TICK_MS) {
        lastCommit = frameTime;
        setMs(Date.now());
      }
      frameId = requestAnimationFrame(tick);
    };
    setMs(Date.now());
    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [enabled]);

  return ms;
};

function isClassificationEnvelope(
  value: unknown,
): value is ProjectFileClassificationEnvelope {
  if (typeof value !== 'object' || value === null) return false;
  return 'classifier' in value && 'data' in value;
}

/** Short label for classifier `document_kind` enum (tooltip uses {@link PROJECT_FILE_DOCUMENT_KIND_AI_GUIDE}). */
const formatDocumentKindKey = (kind: string): string => kind.replace(/_/g, ' ');

const getDocumentKindTooltip = (kind: string): string | undefined => {
  const entry =
    PROJECT_FILE_DOCUMENT_KIND_AI_GUIDE[
      kind as keyof typeof PROJECT_FILE_DOCUMENT_KIND_AI_GUIDE
    ];
  return entry?.[0];
};

/** Classification finished for this upload — classifier whole-file labels are persisted. */
const fileClassificationLabelsAvailable = (
  state: ProjectFileBatchSnapshot['files'][number]['state'],
): boolean =>
  state === 'classified' ||
  state === 'extracting' ||
  state === 'done' ||
  state === 'failed' ||
  state === 'cancelled';

type ProjectFileSnapshotRow = ProjectFileBatchSnapshot['files'][number];

const documentKindSkeleton = (f: ProjectFileSnapshotRow): boolean =>
  (f.state === 'classifying' || f.state === 'preprocessing') &&
  f.classifierDocumentKind == null;

const classificationsSkeleton = (f: ProjectFileSnapshotRow): boolean =>
  (f.state === 'classifying' || f.state === 'preprocessing') &&
  f.classifications.length === 0;

const extractionSkeleton = (f: ProjectFileSnapshotRow): boolean =>
  f.state === 'extracting' && f.extractionOutput == null;

const showConsolidatedOutputSection = (
  batch: ProjectFileBatchSnapshot['batch'],
): boolean => batch.aggregatedOutput != null || batch.state === 'consolidating';

/** Truncate long ids for chips and header lines. */
const shortenId = (id: string | undefined): string => {
  if (id == null || id === '') return '';
  return id.length > 14 ? `${id.slice(0, 12)}…` : id;
};

const MAX_EXTRACTION_VALUE_LEN = 2000;

const humanizeExtractionKey = (k: string): string =>
  k
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

const isPoint2dArray = (
  v: unknown[],
): v is { readonly x: number; readonly y: number }[] =>
  v.every(
    (p) =>
      typeof p === 'object' &&
      p !== null &&
      'x' in p &&
      'y' in p &&
      typeof (p as { x: unknown }).x === 'number' &&
      typeof (p as { y: unknown }).y === 'number',
  );

const truncateDisplay = (s: string): string =>
  s.length <= MAX_EXTRACTION_VALUE_LEN
    ? s
    : `${s.slice(0, MAX_EXTRACTION_VALUE_LEN)}…`;

type ExtractionDetailLine = { label: string; value: string };

type ExtractionSegmentDataClasses = Pick<
  ReturnType<typeof useStyles>['classes'],
  'classificationDetailLine' | 'classificationDetailLabel'
>;

/**
 * Flatten nested extraction payloads into labeled lines — same readability pattern as
 * classification optional-detail rows in {@link FileClassificationCard}.
 */
const collectExtractionDetailLines = (
  obj: Record<string, unknown>,
  lines: ExtractionDetailLine[],
  prefix = '',
): void => {
  for (const [k, v] of Object.entries(obj)) {
    if (isDroppedBlueprintExtractionKey(k)) continue;
    if (prefix === '' && (k === 'kind' || k === 'building_part_requirements'))
      continue;
    const path = prefix
      ? `${prefix} · ${humanizeExtractionKey(k)}`
      : humanizeExtractionKey(k);
    if (v === null || v === undefined) continue;
    if (
      typeof v === 'string' ||
      typeof v === 'number' ||
      typeof v === 'boolean'
    ) {
      lines.push({ label: path, value: truncateDisplay(String(v)) });
      continue;
    }
    if (Array.isArray(v)) {
      if (v.length === 0) {
        lines.push({ label: path, value: '—' });
        continue;
      }
      if (v.every((x) => typeof x === 'number')) {
        lines.push({
          label: path,
          value: truncateDisplay((v as number[]).map(String).join(', ')),
        });
        continue;
      }
      if (v.every((x) => typeof x === 'string')) {
        lines.push({
          label: path,
          value: truncateDisplay((v as string[]).join('; ')),
        });
        continue;
      }
      if (isPoint2dArray(v)) {
        lines.push({
          label: path,
          value: truncateDisplay(v.map((p) => `(${p.x}, ${p.y})`).join(' · ')),
        });
        continue;
      }
      if (
        v.every((x) => x !== null && typeof x === 'object' && !Array.isArray(x))
      ) {
        v.forEach((item, i) => {
          collectExtractionDetailLines(
            item as Record<string, unknown>,
            lines,
            `${path} · ${i + 1}`,
          );
        });
        continue;
      }
      lines.push({
        label: path,
        value: truncateDisplay(v.map((x) => JSON.stringify(x)).join(' · ')),
      });
      continue;
    }
    if (typeof v === 'object') {
      collectExtractionDetailLines(v as Record<string, unknown>, lines, path);
    }
  }
};

const ExtractionSegmentDataLines: React.FC<{
  classes: ExtractionSegmentDataClasses;
  data: Record<string, unknown>;
}> = ({ classes, data }) => {
  const lines: ExtractionDetailLine[] = [];
  collectExtractionDetailLines(data, lines);
  if (lines.length === 0) {
    return (
      <Typography
        variant="caption"
        component="div"
        sx={{
          color: 'text.secondary',
        }}
      >
        No text fields for this slice
      </Typography>
    );
  }
  return (
    <Box
      sx={{
        mt: 1,
      }}
    >
      {lines.map(({ label, value }, i) => (
        <Typography
          key={`${label}-${i}`}
          component="div"
          variant="body2"
          className={classes.classificationDetailLine}
        >
          <Box component="span" className={classes.classificationDetailLabel}>
            {label}
          </Box>
          {value}
        </Typography>
      ))}
    </Box>
  );
};

type ClassificationCardClasses = Pick<
  ReturnType<typeof useStyles>['classes'],
  | 'classificationCard'
  | 'classificationChipRow'
  | 'classificationMeta'
  | 'classificationDetailLine'
  | 'classificationDetailLabel'
>;

const FileClassificationCard: React.FC<{
  classes: ClassificationCardClasses;
  entry: ProjectFileClassificationEnvelope;
}> = ({ classes, entry }) => {
  const { classifier, data } = entry;

  const optionalDetail = (label: string, value?: string | null) => {
    const s = value != null ? String(value).trim() : '';
    if (s === '') return null;
    return (
      <Typography
        component="div"
        variant="body2"
        className={classes.classificationDetailLine}
      >
        <Box component="span" className={classes.classificationDetailLabel}>
          {label}
        </Box>
        {s}
      </Typography>
    );
  };

  const metaBits: string[] = [];
  if (classifier.llmProvider) metaBits.push(classifier.llmProvider);
  if (classifier.model && classifier.model.trim() !== '')
    metaBits.push(classifier.model);
  if (classifier.promptVersion != null && classifier.promptVersion !== '')
    metaBits.push(classifier.promptVersion);
  const runShort = shortenId(classifier.runId);
  if (runShort !== '') metaBits.push(`Run ${runShort}`);

  return (
    <Box className={classes.classificationCard}>
      <Box className={classes.classificationChipRow}>
        <Label
          label={classificationTypeLabel(data.type, data.blueprintSubtype)}
          color="primary"
        />
        <Label label={`${Math.round(data.confidence * 100)}% sure`} />
        <Label
          label={data.confirmed ? 'Reviewed' : 'Not reviewed'}
          color={data.confirmed ? 'success' : 'default'}
        />
      </Box>
      <Typography className={classes.classificationMeta} component="div">
        {metaBits.join(' · ')}
      </Typography>
      {optionalDetail('Page', data.pageHint)}
      {optionalDetail('Building ID (internal)', data.buildingInstanceId)}
      {optionalDetail('Name on drawing', data.buildingLabel)}
      {optionalDetail('Notes', data.notes)}
      {optionalDetail('Quote from file', data.evidenceExcerpt)}
    </Box>
  );
};

type JsonViewportClasses = Pick<
  ReturnType<typeof useStyles>['classes'],
  'jsonScrollViewport' | 'jsonPreInViewport'
>;

const JsonViewport: React.FC<{
  classes: JsonViewportClasses;
  value: unknown;
}> = ({ classes, value }) => (
  <Box className={classes.jsonScrollViewport}>
    <Box component="pre" className={classes.jsonPreInViewport}>
      {JSON.stringify(value, null, 2)}
    </Box>
  </Box>
);

const readNullableOptionalString = (v: unknown): string | null | undefined => {
  if (v === undefined) return undefined;
  if (v === null) return null;
  if (typeof v === 'string') return v;
  return undefined;
};

const normalizeBuildingCompositionsParse = (
  raw: unknown,
): ProjectFileBatchBuildingComposition[] => {
  if (!Array.isArray(raw)) return [];
  const out: ProjectFileBatchBuildingComposition[] = [];
  for (const row of raw) {
    const parsedRow = projectFileBatchBuildingCompositionSchema.safeParse(row);
    if (parsedRow.success) out.push(parsedRow.data);
  }
  return out;
};

const normalizeJoinedBlueprintExtractionsParse = (
  raw: unknown,
): ProjectFileBatchJoinedBlueprintExtraction[] => {
  if (!Array.isArray(raw)) return [];
  const out: ProjectFileBatchJoinedBlueprintExtraction[] = [];
  for (const row of raw) {
    const parsedRow =
      projectFileBatchJoinedBlueprintExtractionSchema.safeParse(row);
    if (parsedRow.success) out.push(parsedRow.data);
  }
  return out;
};

const normalizeBuildingPartRequirementsParse = (
  raw: unknown,
): BuildingPartRequirementAggregated[] => {
  if (!Array.isArray(raw)) return [];
  const out: BuildingPartRequirementAggregated[] = [];
  for (const row of raw) {
    const parsedRow = buildingPartRequirementAggregatedSchema.safeParse(row);
    if (parsedRow.success) out.push(parsedRow.data);
  }
  return mergeBuildingPartRequirementRows(out);
};

const normalizeDesignFactConflictsParse = (
  raw: unknown,
): ExtractedDesignFactConflict[] => {
  if (!Array.isArray(raw)) return [];
  const out: ExtractedDesignFactConflict[] = [];
  for (const row of raw) {
    const parsedRow = extractedDesignFactConflictSchema.safeParse(row);
    if (parsedRow.success) out.push(parsedRow.data);
  }
  return out;
};

type OutputMetric = { label: string; value: string; meta?: string };

const formatCount = (n: number): string =>
  Number.isInteger(n) ? String(n) : String(Math.round(n * 10) / 10);

const formatMeters = (n: number): string => `${formatCount(n)} m`;

const formatArea = (n: number): string => `${formatCount(n)} m²`;

const formatWindowRatio = (n: number): string => {
  if (n >= 0 && n <= 1) return `${Math.round(n * 100)} %`;
  return formatCount(n);
};

/** The building's figures as the output stores them. */
const ffuBuildingMetrics = (
  building: FfuBuilding,
  joined: ProjectFileBatchJoinedBlueprintExtraction | null,
): OutputMetric[] => {
  const out: OutputMetric[] = [];
  const add = (label: string, value: string | null, meta?: string): void => {
    if (value == null || value === '') return;
    if (meta == null || meta === '') {
      out.push({ label, value });
      return;
    }
    out.push({ label, value, meta });
  };
  const { areas, roof, storeys } = building.geometry;
  if (joined != null) {
    if (joined.overallHeightM != null)
      add('Höjd', formatMeters(joined.overallHeightM));
    if (
      joined.storeyCount != null &&
      building.storeyCount == null &&
      storeys.length === 0
    ) {
      add('Våningar', formatCount(joined.storeyCount));
    }
  }
  if (building.storeyCount != null) {
    add('Våningar', formatStoreyCount(building.storeyCount));
  }
  if (building.connection != null) {
    add('Sammanbyggd', building.connection.note);
  }
  if (areas.gfa != null) add('BTA', formatArea(areas.gfa));
  if (areas.gfa_above_ground != null)
    add('Ljus BTA', formatArea(areas.gfa_above_ground));
  if (areas.livingAreaM2 != null) add('BOA', formatArea(areas.livingAreaM2));
  if (areas.premisesAreaM2 != null)
    add('LOA', formatArea(areas.premisesAreaM2));
  if (areas.footprintM2 != null) add('BYA', formatArea(areas.footprintM2));
  if (building.apartmentCount != null) {
    add('Lägenheter', formatCount(building.apartmentCount));
  }
  const windowRatio = building.properties.window_ratio;
  if (windowRatio != null) {
    add(
      'Fönster',
      formatWindowRatio(windowRatio.value),
      windowRatio.determined_by,
    );
  }
  if (building.beamSpanM != null)
    add('Balkspann', formatMeters(building.beamSpanM));
  if (building.slabThicknessMm != null) {
    add('Bjälklag', `${formatCount(building.slabThicknessMm)} mm`);
  }
  if (roof != null) {
    const label = formatFfuRoof(roof);
    add('Takform', label.fact, label.meta);
  }
  if (building.residential?.livingAreaPerApartmentM2 != null) {
    add('Lägenhet', formatArea(building.residential.livingAreaPerApartmentM2));
  }
  if (building.residential?.balconyAreaPerApartmentM2 != null) {
    add('Balkong', formatArea(building.residential.balconyAreaPerApartmentM2));
  }
  if (building.stairwells?.apartmentsPerStairwellPerStorey != null) {
    add(
      'Lgh/trapphus',
      formatCount(building.stairwells.apartmentsPerStairwellPerStorey),
    );
  }
  if (building.stairwells?.elevatorsPerStairwell != null) {
    add('Hissar', formatCount(building.stairwells.elevatorsPerStairwell));
  }
  return out;
};

const joinedGeometryMetrics = (
  joined: ProjectFileBatchJoinedBlueprintExtraction,
): OutputMetric[] => {
  const out: OutputMetric[] = [];
  if (joined.overallHeightM != null) {
    out.push({ label: 'Höjd', value: formatMeters(joined.overallHeightM) });
  }
  if (joined.storeyCount != null) {
    out.push({ label: 'Våningar', value: formatCount(joined.storeyCount) });
  }
  return out;
};

const geometryFailureNotice = (count: number): string | null => {
  if (count === 0) return null;
  if (count === 1) return 'Geometri saknas för en grupp.';
  return `Geometri saknas för ${count} grupper.`;
};

const OutputMetricRow: React.FC<{ items: readonly OutputMetric[] }> = ({
  items,
}) => {
  const { classes } = useStyles();
  if (items.length === 0) return null;
  return (
    <div className={classes.outputMetricRow}>
      {items.map((item) => (
        <div key={item.label} className={classes.outputMetric}>
          <span className={classes.outputMetricValue}>{item.value}</span>
          {item.meta != null && (
            <span className={classes.outputMetricMeta}>{item.meta}</span>
          )}
          <span className={classes.outputMetricLabel}>{item.label}</span>
        </div>
      ))}
    </div>
  );
};

const AiStepCostTable: React.FC<{ calls: readonly ProjectFileAiCall[] }> = ({
  calls,
}) => {
  const { classes } = useStyles();
  const rows = useMemo(
    () => summarizeProjectFileAiCostByStep(calls).map(aiStepCostRow),
    [calls],
  );
  if (rows.length === 0) return null;
  return (
    <Box className={`${classes.section} ${classes.outputSection}`}>
      <p className={classes.outputCaption}>AI cost per step</p>
      <div className={classes.outputTableWrap}>
        <table className={classes.outputTable}>
          <thead>
            <tr>
              <th scope="col">Step</th>
              <th scope="col" className={classes.outputCellRight}>
                Calls
              </th>
              <th scope="col" className={classes.outputCellRight}>
                Cost
              </th>
              <th scope="col" className={classes.outputCellRight}>
                Input
              </th>
              <th scope="col" className={classes.outputCellRight}>
                Cached
              </th>
              <th scope="col" className={classes.outputCellRight}>
                Output
              </th>
              <th scope="col" className={classes.outputCellRight}>
                Over 272k input
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.step}>
                <td>{row.label}</td>
                <td className={classes.outputCellRight}>{row.calls}</td>
                <td className={classes.outputCellRight}>{row.usd}</td>
                <td className={classes.outputCellRight}>{row.input}</td>
                <td className={classes.outputCellRight}>{row.cached}</td>
                <td className={classes.outputCellRight}>{row.output}</td>
                <td className={classes.outputCellRight}>{row.longContext}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Box>
  );
};

const OutputStoreyTable: React.FC<{
  storeys: readonly BuildingStoreyRow[];
  caption: string;
}> = ({ storeys, caption }) => {
  const { classes } = useStyles();
  const table = storeyTable(storeys);
  if (table.rows.length === 0) return null;
  return (
    <div className={classes.outputSection}>
      <p className={classes.outputCaption}>{caption}</p>
      <div className={classes.outputTableWrap}>
        <table className={classes.outputTable}>
          <thead>
            {table.headerRows.map((headerRow) => (
              <tr key={headerRow.map((cell) => cell.id).join(':')}>
                {headerRow.map((cell) => (
                  <th
                    key={cell.id}
                    scope={cell.colSpan > 1 ? 'colgroup' : 'col'}
                    colSpan={cell.colSpan > 1 ? cell.colSpan : undefined}
                    rowSpan={cell.rowSpan > 1 ? cell.rowSpan : undefined}
                    className={
                      cell.align === 'right'
                        ? classes.outputCellRight
                        : undefined
                    }
                  >
                    {cell.label}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.rows.map((row) => (
              <tr
                key={row.key}
                className={
                  row.entrance ? classes.outputStoreyGround : undefined
                }
              >
                {table.columns.map((col, index) => (
                  <td
                    key={col.id}
                    className={
                      col.align === 'right'
                        ? classes.outputCellRight
                        : undefined
                    }
                  >
                    {row.cells[index]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const OutputRequirementCard: React.FC<{
  row: BuildingPartRequirementAggregated;
}> = ({ row }) => {
  const { classes, cx } = useStyles();
  const fields = requirementCardFields(row);
  if (fields.length === 0) return null;
  return (
    <div className={cx(classes.outputMetric, classes.outputRequirementCard)}>
      {fields.map((field) => (
        <div key={field.label} className={classes.outputRequirementField}>
          <span className={classes.outputMetricLabel}>{field.label}</span>
          <span className={classes.outputMetricValue}>{field.value}</span>
        </div>
      ))}
    </div>
  );
};

const OutputRequirementCards: React.FC<{
  rows: readonly BuildingPartRequirementAggregated[];
}> = ({ rows }) => {
  const { classes } = useStyles();
  const cards = [...buildingPartRequirementCards(rows)].sort((a, b) =>
    formatRequirementCardHeading(a).localeCompare(
      formatRequirementCardHeading(b),
      'sv',
    ),
  );
  if (cards.length === 0) return null;
  return (
    <section className={classes.outputSection}>
      <h3 className={classes.outputSectionTitle}>Krav</h3>
      <div className={classes.outputMetricRow}>
        {cards.map((row, i) => (
          <OutputRequirementCard
            key={`${formatRequirementCardHeading(row)}-${row.sourceFileId}-${i}`}
            row={row}
          />
        ))}
      </div>
    </section>
  );
};

const planNameKey = (name: string): string =>
  extractedBuildingDisplayGroupKey({
    buildingName: name,
    buildingInstanceKey: null,
  });

const assignPlanGroups = (
  sections: readonly {
    name: string;
    hasStoreys: boolean;
    building: { instanceKeys: readonly string[] };
  }[],
  leftovers: readonly {
    buildingInstanceKey: string | null;
    buildingLabel: string | null;
  }[],
  groups: readonly PlanOverlayGroup[],
): {
  bySection: PlanOverlayGroup[][];
  byLeftover: PlanOverlayGroup[][];
  orphans: PlanOverlayGroup[];
} => {
  const used = new Set<string>();
  const take = (keys: readonly string[]): PlanOverlayGroup[] => {
    const wanted = new Set(keys.filter((key) => key !== '' && key !== '_'));
    const hit = groups.filter(
      (group) => !used.has(group.key) && wanted.has(group.key),
    );
    for (const group of hit) used.add(group.key);
    return hit;
  };
  const bySection = sections.map((section) =>
    take(section.building.instanceKeys),
  );
  for (const group of groups) {
    if (used.has(group.key)) continue;
    const label = group.label.trim();
    if (label === '' || label === 'Planritningar') continue;
    const labelKey = planNameKey(label);
    const sectionIndex = sections.findIndex(
      (section) =>
        section.name !== '' && planNameKey(section.name) === labelKey,
    );
    if (sectionIndex < 0) continue;
    bySection[sectionIndex]?.push(group);
    used.add(group.key);
  }
  const onlyStoreySection = soleStoreySectionIndex(
    sections.map((section) => section.hasStoreys),
  );
  const onlyBucket =
    onlyStoreySection === null ? undefined : bySection[onlyStoreySection];
  if (onlyBucket !== undefined) {
    for (const group of groups) {
      if (used.has(group.key)) continue;
      onlyBucket.push(group);
      used.add(group.key);
    }
  }
  const byLeftover = leftovers.map((joined) =>
    take(
      joined.buildingInstanceKey != null ? [joined.buildingInstanceKey] : [],
    ),
  );
  for (const group of groups) {
    if (used.has(group.key)) continue;
    const label = group.label.trim();
    if (label === '' || label === 'Planritningar') continue;
    const labelKey = planNameKey(label);
    const leftoverIndex = leftovers.findIndex((joined) => {
      const name = joined.buildingLabel?.trim() ?? '';
      return name !== '' && planNameKey(name) === labelKey;
    });
    if (leftoverIndex < 0) continue;
    byLeftover[leftoverIndex]?.push(group);
    used.add(group.key);
  }
  return {
    bySection,
    byLeftover,
    orphans: groups.filter((group) => !used.has(group.key)),
  };
};

const OutputKeys: React.FC<{ keys: readonly string[] }> = ({ keys }) => {
  const { classes } = useStyles();
  const visible = keys.filter((key) => key !== '' && key !== '_');
  if (visible.length === 0) return null;
  return (
    <>
      {visible.map((key) => (
        <span key={key} className={classes.outputKey}>
          {key}
        </span>
      ))}
    </>
  );
};

const readFfuFacts = (raw: unknown): FfuOutputFacts | null => {
  const parsed = ffuOutputFactsSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
};

type FfuBuildingSection = {
  building: FfuBuilding;
  metrics: OutputMetric[];
  showName: boolean;
  name: string;
  hasStoreys: boolean;
};

const FfuBuildingSections: React.FC<{
  facts: FfuOutputFacts;
  pageTitle: string;
  joinedBlueprintExtractions: readonly ProjectFileBatchJoinedBlueprintExtraction[];
  plans: { batchId: string; groups: readonly PlanOverlayGroup[] } | null;
}> = ({ facts, pageTitle, joinedBlueprintExtractions, plans }) => {
  const { classes } = useStyles();
  const usedJoined = new Set<ProjectFileBatchJoinedBlueprintExtraction>();
  const takeJoined = (
    building: FfuBuilding,
  ): ProjectFileBatchJoinedBlueprintExtraction | null => {
    const hit = joinedBlueprintExtractions.find(
      (row) =>
        !usedJoined.has(row) &&
        row.buildingInstanceKey != null &&
        building.instanceKeys.includes(row.buildingInstanceKey),
    );
    if (hit != null) {
      usedJoined.add(hit);
      return hit;
    }
    const only = joinedBlueprintExtractions[0];
    if (
      facts.buildings.length === 1 &&
      joinedBlueprintExtractions.length === 1 &&
      only != null &&
      !usedJoined.has(only)
    ) {
      usedJoined.add(only);
      return only;
    }
    return null;
  };
  const sections = facts.buildings.flatMap((building): FfuBuildingSection[] => {
    const joined = takeJoined(building);
    const metrics = ffuBuildingMetrics(building, joined);
    const hasStoreys =
      building.geometry.storeys.length > 0 ||
      building.geometry.parts.some((part) => part.storeys.length > 0);
    if (metrics.length === 0 && !hasStoreys) return [];
    const name = building.buildingName?.trim() ?? '';
    const showName =
      facts.buildings.length > 1 ||
      (name !== '' && name.toLowerCase() !== pageTitle.toLowerCase());
    return [{ building, metrics, showName, name, hasStoreys }];
  });
  const leftoverJoined = joinedBlueprintExtractions.filter(
    (row) => !usedJoined.has(row) && joinedGeometryMetrics(row).length > 0,
  );
  const placedPlans =
    plans == null
      ? null
      : assignPlanGroups(sections, leftoverJoined, plans.groups);
  return (
    <>
      {sections.map((section, i) => {
        const sectionGroups = placedPlans?.bySection[i] ?? [];
        const sheets = sectionGroups.flatMap((group) => group.sheets);
        const storeyRows = buildingStoreyRows({
          storeys: section.building.geometry.storeys,
          parts: section.building.geometry.parts,
        });
        const tableGroups = storeyGroups(storeyRows);
        const storeyPreview =
          tableGroups.length === 0
            ? null
            : buildStoreyPreviews({ groups: tableGroups, sheets });
        return (
          <section
            key={`${section.building.instanceKeys.join('-')}-${i}`}
            className={classes.outputSection}
          >
            {section.showName && (
              <h3 className={classes.outputSectionTitle}>
                {section.name !== '' ? section.name : 'Hus'}
                <OutputKeys keys={section.building.instanceKeys} />
              </h3>
            )}
            <div className={classes.outputBuildingStack}>
              <OutputMetricRow items={section.metrics} />
              <div className={classes.outputBuildingRow}>
                <div className={classes.outputBuildingFacts}>
                  <OutputStoreyTable storeys={storeyRows} caption="Våningar" />
                </div>
                {placedPlans != null && plans != null && (
                  <div className={classes.outputBuildingPlans}>
                    <PlanOverlayStages
                      batchId={plans.batchId}
                      groups={sectionGroups}
                      storeyPreviews={storeyPreview?.previews}
                      embedded
                    />
                  </div>
                )}
              </div>
            </div>
          </section>
        );
      })}
      {leftoverJoined.map((joined, i) => (
        <section
          key={`${joined.buildingInstanceKey ?? 'geom'}-${i}`}
          className={classes.outputSection}
        >
          <h3 className={classes.outputSectionTitle}>
            {joined.buildingLabel?.trim() || 'Geometri'}
            <OutputKeys
              keys={
                joined.buildingInstanceKey != null
                  ? [joined.buildingInstanceKey]
                  : []
              }
            />
          </h3>
          <div className={classes.outputBuildingRow}>
            <div className={classes.outputBuildingFacts}>
              <OutputMetricRow items={joinedGeometryMetrics(joined)} />
            </div>
            {placedPlans != null &&
              plans != null &&
              (placedPlans.byLeftover[i]?.length ?? 0) > 0 && (
                <div className={classes.outputBuildingPlans}>
                  <PlanOverlayStages
                    batchId={plans.batchId}
                    groups={placedPlans.byLeftover[i] ?? []}
                    embedded
                  />
                </div>
              )}
          </div>
        </section>
      ))}
      {placedPlans != null &&
        plans != null &&
        placedPlans.orphans.map((group) => (
          <section key={group.key} className={classes.outputSection}>
            <h3 className={classes.outputSectionTitle}>
              {group.label}
              <OutputKeys keys={[group.key]} />
            </h3>
            <div className={classes.outputBuildingPlans}>
              <PlanOverlayStages
                batchId={plans.batchId}
                groups={[group]}
                embedded
              />
            </div>
          </section>
        ))}
    </>
  );
};

/**
 * A batch's output. The final output (`final_output`, one house per building)
 * is shown first; the pipeline output with its parts below, collapsed. Both
 * are read as stored. A batch from before the stored FFU output shows its raw
 * data and plans only.
 */
const BatchAggregatedOutputView: React.FC<{
  value: unknown;
  /** `project_file_batches.final_output`. */
  finalOutput?: unknown;
  plans: { batchId: string; groups: readonly PlanOverlayGroup[] } | null;
}> = ({ value, finalOutput, plans }) => {
  const { classes } = useStyles();
  const rawData = (
    <details className={classes.timelineRawWrap}>
      <summary className={classes.timelineRawSummary}>Rådata</summary>
      <JsonViewport
        classes={{
          jsonScrollViewport: classes.jsonScrollViewport,
          jsonPreInViewport: classes.jsonPreInViewport,
        }}
        value={value}
      />
    </details>
  );
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return (
      <>
        {rawData}
        {plans != null && (
          <PlanOverlayStages batchId={plans.batchId} groups={plans.groups} />
        )}
      </>
    );
  }

  const raw = value as Record<string, unknown>;
  const parsed = projectFileBatchAggregatedOutputSchema.safeParse(value);
  const ok = parsed.success ? parsed.data : null;
  const pipelineFacts = readFfuFacts(
    ok?.extractedDesignFacts ?? raw.extractedDesignFacts,
  );
  const finalFacts = readFfuFacts(finalOutput);
  const shown = finalFacts ?? pipelineFacts;

  if (shown == null) {
    return (
      <div className={classes.outputRoot}>
        <p className={classes.outputWarn}>
          Batchen sparades i ett äldre format, före FFU-outputen (7 okt). Visar
          rådata och planer.
        </p>
        {plans != null && (
          <PlanOverlayStages batchId={plans.batchId} groups={plans.groups} />
        )}
        {rawData}
      </div>
    );
  }

  const suggestedProjectName =
    ok?.suggestedProjectName ??
    readNullableOptionalString(raw.suggestedProjectName);
  const suggestedProjectDescription =
    ok?.suggestedProjectDescription ??
    readNullableOptionalString(raw.suggestedProjectDescription);
  const buildingCompositions = normalizeBuildingCompositionsParse(
    ok?.buildingCompositions ?? raw.buildingCompositions,
  );
  const joinedBlueprintExtractions = normalizeJoinedBlueprintExtractionsParse(
    ok?.joinedBlueprintExtractions ?? raw.joinedBlueprintExtractions,
  );
  const buildingPartRequirements = normalizeBuildingPartRequirementsParse(
    ok?.buildingPartRequirements ?? raw.buildingPartRequirements,
  );
  const designFactConflicts = normalizeDesignFactConflictsParse(
    ok?.conflicts ?? raw.conflicts,
  );

  const pageTitle =
    (typeof suggestedProjectName === 'string'
      ? suggestedProjectName.trim()
      : '') ||
    shown.name?.trim() ||
    '';
  const location = shown.location?.trim() ?? '';
  const description =
    (typeof suggestedProjectDescription === 'string'
      ? suggestedProjectDescription.trim()
      : '') ||
    shown.description?.trim() ||
    '';
  const showLocation =
    location !== '' && location.toLowerCase() !== pageTitle.toLowerCase();
  const modelAsOne = buildingCompositions.some(
    (row) => row.shouldModelAsSingleBuilding,
  );
  const geometryFailureText = geometryFailureNotice(
    ok?.joinedBlueprintExtractionFailures?.length ?? 0,
  );
  const tableView = readFfuBatchOutput(
    finalOutput != null && finalFacts != null ? finalOutput : raw,
  );

  return (
    <div className={classes.outputRoot}>
      {(pageTitle !== '' || showLocation || description !== '') && (
        <header className={classes.outputHeader}>
          {pageTitle !== '' && (
            <h2 className={classes.outputTitle}>{pageTitle}</h2>
          )}
          {showLocation && <p className={classes.outputLocation}>{location}</p>}
          {description !== '' && (
            <p className={classes.outputLead} title={description}>
              {description}
            </p>
          )}
        </header>
      )}
      {finalFacts == null && (
        <p className={classes.outputWarn}>
          Ingen final output sparad för batchen. Visar pipelinens output.
        </p>
      )}
      {geometryFailureText != null && (
        <p className={classes.outputWarn}>{geometryFailureText}</p>
      )}
      {modelAsOne && (
        <OutputMetricRow items={[{ label: 'Modell', value: 'Ett hus' }]} />
      )}
      <FfuBuildingSections
        facts={shown}
        pageTitle={pageTitle}
        joinedBlueprintExtractions={joinedBlueprintExtractions}
        plans={plans}
      />
      <OutputRequirementCards rows={buildingPartRequirements} />
      {finalFacts != null && pipelineFacts != null && (
        <details className={classes.timelineRawWrap}>
          <summary className={classes.timelineRawSummary}>
            Pipelinens output (med delar)
          </summary>
          <FfuBuildingSections
            facts={pipelineFacts}
            pageTitle={pageTitle}
            joinedBlueprintExtractions={[]}
            plans={null}
          />
        </details>
      )}
      {tableView != null && (
        <details className={classes.timelineRawWrap}>
          <summary className={classes.timelineRawSummary}>
            Tabellvy (alla fält)
          </summary>
          <FfuOutputView output={tableView} />
        </details>
      )}
      {designFactConflicts.length > 0 && (
        <details className={classes.timelineRawWrap}>
          <summary className={classes.timelineRawSummary}>Motstridigt</summary>
          <ul className={classes.outputConflictList}>
            {designFactConflicts.map((row) => (
              <li key={row.path}>
                {extractedDesignFactPathLabel(row.path)}
                {': '}
                {formatExtractedDesignFactConflictNotice(row).summary}
              </li>
            ))}
          </ul>
        </details>
      )}
      {rawData}
    </div>
  );
};

type ExtractionViewClasses = Pick<
  ReturnType<typeof useStyles>['classes'],
  | 'extractionSegmentsGrid'
  | 'extractionSegmentCard'
  | 'extractionSegmentCardOk'
  | 'extractionSegmentCardFail'
  | 'extractionChipRow'
  | 'extractionMeta'
  | 'extractionErrorLine'
  | 'classificationDetailLine'
  | 'classificationDetailLabel'
  | 'jsonScrollViewport'
  | 'jsonPreInViewport'
>;

type ExtractionV1Segment = ProjectFileExtractionOutputV1['segments'][number];

const parseSegmentBuildingPartRequirements = (
  data: Record<string, unknown>,
): BuildingPartRequirement[] => {
  const raw = data.building_part_requirements;
  if (!Array.isArray(raw)) return [];
  const out: BuildingPartRequirement[] = [];
  for (const item of raw) {
    const parsed = parseBuildingPartRequirement(item);
    if (parsed !== null) out.push(parsed);
  }
  return out;
};

const SegmentBuildingPartRequirements: React.FC<{
  classes: Pick<
    ExtractionViewClasses,
    'classificationDetailLine' | 'classificationDetailLabel'
  >;
  rows: readonly BuildingPartRequirement[];
}> = ({ classes, rows }) => {
  if (rows.length === 0) return null;
  return (
    <div>
      {rows.map((row, i) => (
        <div
          key={
            isElementRequirement(row)
              ? `${row.buildingId}-${row.place}-${row.sbef}-${row.name}-${i}`
              : `${row.typcode ?? row.source_label}-${i}`
          }
        >
          <Typography
            component="div"
            variant="body2"
            className={classes.classificationDetailLine}
          >
            <Box component="span" className={classes.classificationDetailLabel}>
              {formatStoredRequirementScopeCaption(row)}
            </Box>
            {formatBuildingPartTarget(row)}
          </Typography>
          <Typography
            component="div"
            variant="body2"
            className={classes.classificationDetailLine}
          >
            <Box component="span" className={classes.classificationDetailLabel}>
              Krav
            </Box>
            {formatBuildingPartConstraint(row.constraint)}
          </Typography>
        </div>
      ))}
    </div>
  );
};

const ExtractionSegmentCard: React.FC<{
  classes: ExtractionViewClasses;
  cx: (...args: Parameters<ReturnType<typeof useStyles>['cx']>) => string;
  segment: ExtractionV1Segment;
  blueprintSubtype?: string | null;
}> = ({ classes, cx, segment: seg, blueprintSubtype }) => (
  <Box
    className={cx(
      classes.extractionSegmentCard,
      seg.ok
        ? classes.extractionSegmentCardOk
        : classes.extractionSegmentCardFail,
    )}
  >
    <Box className={classes.extractionChipRow}>
      <Label
        title="classificationSegmentIndex — same index as the classifier segment row this run used."
        label={`#${seg.classificationSegmentIndex}`}
      />
      <Label
        label={classificationTypeLabel(
          seg.classificationType,
          blueprintSubtype,
        )}
        color="primary"
      />
      <Label
        label={seg.ok ? 'Succeeded' : 'Failed'}
        color={seg.ok ? 'success' : 'error'}
      />
    </Box>
    <Typography
      component="div"
      className={classes.extractionMeta}
      title={seg.sourceSubDocumentId}
    >
      Internal slice ID · {shortenId(seg.sourceSubDocumentId)}
    </Typography>
    {!seg.ok && seg.error != null && seg.error !== '' && (
      <Typography
        variant="caption"
        color="error"
        className={classes.extractionErrorLine}
      >
        {seg.error}
      </Typography>
    )}
    {seg.ok &&
      seg.data != null &&
      typeof seg.data === 'object' &&
      !Array.isArray(seg.data) && (
        <>
          <SegmentBuildingPartRequirements
            classes={{
              classificationDetailLine: classes.classificationDetailLine,
              classificationDetailLabel: classes.classificationDetailLabel,
            }}
            rows={parseSegmentBuildingPartRequirements(
              seg.data as Record<string, unknown>,
            )}
          />
          <ExtractionSegmentDataLines
            classes={{
              classificationDetailLine: classes.classificationDetailLine,
              classificationDetailLabel: classes.classificationDetailLabel,
            }}
            data={seg.data as Record<string, unknown>}
          />
        </>
      )}
  </Box>
);

const FileExtractionOutputView: React.FC<{
  classes: ExtractionViewClasses;
  cx: (...args: Parameters<ReturnType<typeof useStyles>['cx']>) => string;
  output: unknown;
}> = ({ classes, cx, output }) => {
  if (isProjectFileExtractionOutputV1(output)) {
    const modelSuffix =
      output.model != null && output.model !== ''
        ? ` · AI model ${shortenId(output.model)}`
        : '';
    const nSeg = output.segments.length;
    return (
      <Box>
        <Typography component="div" className={classes.extractionMeta}>
          Extraction output (layout v{output.schemaVersion}){modelSuffix} ·{' '}
          {nSeg} segment{nSeg === 1 ? '' : 's'}
        </Typography>
        <Box className={classes.extractionSegmentsGrid}>
          {output.segments.map((seg, idx) => (
            <ExtractionSegmentCard
              key={`seg-${seg.sourceSubDocumentId}-${idx}`}
              classes={classes}
              cx={cx}
              segment={seg}
            />
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <JsonViewport
      classes={{
        jsonScrollViewport: classes.jsonScrollViewport,
        jsonPreInViewport: classes.jsonPreInViewport,
      }}
      value={output}
    />
  );
};

type SegmentPairsClasses = ExtractionViewClasses &
  ClassificationCardClasses &
  Pick<
    ReturnType<typeof useStyles>['classes'],
    | 'segmentPairsList'
    | 'segmentPairRow'
    | 'segmentPairHeader'
    | 'segmentPairCell'
    | 'segmentPairEmptyCell'
    | 'extractionSkeletonBox'
    | 'filePendingBlock'
    | 'classificationFallback'
    | 'jsonScrollViewport'
    | 'jsonPreInViewport'
  >;

/**
 * Per-file "Segments" view: one row per classifier segment, with the
 * classification card on the left and the matching extraction segment card
 * (or skeleton / placeholder) on the right. Replaces the old "Findings" +
 * "Extracted fields" split which made it hard to see which extraction came
 * from which classifier finding. Pairing key is `classificationSegmentIndex`.
 */
const FileSegmentPairs: React.FC<{
  classes: SegmentPairsClasses;
  cx: (...args: Parameters<ReturnType<typeof useStyles>['cx']>) => string;
  file: ProjectFileBatchSnapshot['files'][number];
  extractionPending: boolean;
}> = ({ classes, cx, file, extractionPending }) => {
  const v1Output = isProjectFileExtractionOutputV1(file.extractionOutput)
    ? file.extractionOutput
    : null;
  const segmentByIdx = new Map<number, ExtractionV1Segment>();
  if (v1Output) {
    for (const s of v1Output.segments)
      segmentByIdx.set(s.classificationSegmentIndex, s);
  }
  const pairCount = Math.max(
    file.classifications.length,
    v1Output?.segments.length ?? 0,
  );
  if (pairCount === 0 && !extractionPending && !file.extractionOutput)
    return null;

  return (
    <Box
      sx={{
        mt: 0.5,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          color: 'text.secondary',
        }}
      >
        Segments ({pairCount})
      </Typography>
      <Box className={classes.segmentPairsList}>
        {Array.from({ length: pairCount }).map((_, idx) => {
          const rawClassification = file.classifications[idx];
          const extractionSeg = segmentByIdx.get(idx) ?? null;
          const blueprintSubtype = isClassificationEnvelope(rawClassification)
            ? rawClassification.data.blueprintSubtype
            : null;
          return (
            <Box
              key={`${file.id}-pair-${idx}`}
              className={classes.segmentPairRow}
            >
              <Box className={classes.segmentPairHeader}>
                <Label label={`Segment #${idx}`} />
              </Box>
              <Box className={classes.segmentPairCell}>
                {rawClassification == null ? (
                  <Box className={classes.segmentPairEmptyCell}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      No classification for this segment.
                    </Typography>
                  </Box>
                ) : isClassificationEnvelope(rawClassification) ? (
                  <FileClassificationCard
                    classes={classes}
                    entry={rawClassification}
                  />
                ) : (
                  <Box className={classes.classificationFallback}>
                    <JsonViewport
                      classes={{
                        jsonScrollViewport: classes.jsonScrollViewport,
                        jsonPreInViewport: classes.jsonPreInViewport,
                      }}
                      value={rawClassification}
                    />
                  </Box>
                )}
              </Box>
              <Box className={classes.segmentPairCell}>
                {extractionSeg != null ? (
                  <ExtractionSegmentCard
                    classes={classes}
                    cx={cx}
                    segment={extractionSeg}
                    blueprintSubtype={blueprintSubtype}
                  />
                ) : extractionPending ? (
                  <Box
                    className={classes.filePendingBlock}
                    role="status"
                    aria-label="Working on extracted fields"
                  >
                    <Box className={classes.extractionSkeletonBox}>
                      <Skeleton variant="text" width={180} />
                      <Skeleton variant="rounded" height={88} />
                      <Skeleton variant="rounded" height={72} />
                    </Box>
                  </Box>
                ) : (
                  <Box className={classes.segmentPairEmptyCell}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      No extraction yet.
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
      {file.extractionOutput != null && v1Output == null && (
        <Box
          sx={{
            mt: 1,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
            }}
          >
            Extraction output (legacy / non-v1)
          </Typography>
          <FileExtractionOutputView
            classes={classes}
            cx={cx}
            output={file.extractionOutput}
          />
        </Box>
      )}
    </Box>
  );
};

/** Background color per timeline segment state — batch lane vs file lane palettes. */
const barBgForTimeline = (
  theme: Theme,
  lane: 'batch' | 'file',
  state: string,
): string => {
  const grey = theme.palette.grey;
  const md = theme.palette.mode === 'dark';
  /** index grey by shade without widening `Color` keys to arbitrary numbers */
  const g = (dark: keyof typeof grey, light: keyof typeof grey): string =>
    String(md ? grey[dark] : grey[light]);
  const batch: Record<string, string> = {
    collecting: g(700, 400),
    classifying: theme.palette.primary.main,
    awaiting_confirmation: theme.palette.warning.main,
    extracting: theme.palette.secondary.main,
    consolidating: theme.palette.info.main,
    done: theme.palette.success.main,
    failed: theme.palette.error.main,
    interrupted: theme.palette.warning.dark,
  };
  const file: Record<string, string> = {
    queued: g(700, 400),
    preprocessing: g(700, 600),
    classifying: theme.palette.primary.main,
    classified: theme.palette.info.main,
    extracting: theme.palette.secondary.main,
    done: theme.palette.success.main,
    failed: theme.palette.error.main,
    cancelled: g(800, 500),
  };
  const map = lane === 'batch' ? batch : file;
  return map[state] ?? g(700, 500);
};

type TimelineAxisLaneClasses = Pick<
  ReturnType<typeof useStyles>['classes'],
  | 'timelineUnifiedStack'
  | 'timelineUnifiedRow'
  | 'timelineRowLabelCol'
  | 'timelineRowTrackWrap'
  | 'timelineLaneStacked'
  | 'timelineTrack'
  | 'timelineSegment'
  | 'timelineSegmentLive'
  | 'timelineCaption'
  | 'timelineCaptionSpacerCell'
  | 'timelineSegmentLabel'
>;

const BATCH_SCOPE_PREDICATE = (e: ProjectFileBatchTimelineEntry): boolean =>
  e.scope === 'batch';

const fileScopePredicate =
  (fileId: string) =>
  (e: ProjectFileBatchTimelineEntry): boolean =>
    e.scope === 'file' && e.fileId === fileId;

const UnifiedPipelineTimelines: React.FC<{
  classes: TimelineAxisLaneClasses;
  timeline: readonly ProjectFileBatchTimelineEntry[];
  batchState: ProjectFileBatchState;
  batchCreatedAt: string;
  /** Display order for lanes (after batch): one row per file id. */
  files: ProjectFileBatchSnapshot['files'];
}> = ({ classes, timeline, batchState, batchCreatedAt, files }) => {
  const theme = useTheme();
  const pipelineComplete = isTerminalBatchState(batchState);
  const wallNowMs = useWallClockMs(!pipelineComplete);

  const batchSpans = useMemo(
    () => buildTimelineSpans(timeline, BATCH_SCOPE_PREDICATE, wallNowMs),
    [timeline, wallNowMs],
  );

  const fileSpanGroups = useMemo(
    () =>
      files.map((f) =>
        buildTimelineSpans(timeline, fileScopePredicate(f.id), wallNowMs),
      ),
    [timeline, files, wallNowMs],
  );

  const { minTs, maxTs } = useMemo(
    () =>
      computePipelineTimelineExtents(
        timeline,
        [batchSpans, ...fileSpanGroups],
        wallNowMs,
        [batchCreatedAt],
        { pipelineComplete },
      ),
    [
      timeline,
      batchSpans,
      fileSpanGroups,
      wallNowMs,
      batchCreatedAt,
      pipelineComplete,
    ],
  );

  const t0 = formatTime(new Date(minTs).toISOString());
  const t1 = formatTime(new Date(maxTs).toISOString());

  const renderLaneRow = (
    rowKey: string,
    rowLabel: string,
    spans: readonly ProjectFileTimelineSpan[],
    lane: 'batch' | 'file',
  ) => (
    <Box key={rowKey} className={classes.timelineUnifiedRow}>
      <Box
        className={classes.timelineRowLabelCol}
        component="div"
        title={rowLabel}
      >
        {rowLabel}
      </Box>
      <Box className={classes.timelineRowTrackWrap}>
        <Box className={classes.timelineLaneStacked}>
          <Box className={classes.timelineTrack}>
            {layoutTimelineSegments(spans, minTs, maxTs).map((seg, i) => {
              const span = spans[i];
              if (span === undefined) return null;
              const bg = barBgForTimeline(theme, lane, span.state);
              const fg = theme.palette.getContrastText(bg);
              const durationMs = span.endMs - span.startMs;
              const durationLabel = formatPhaseDurationMs(durationMs);
              const readable = friendlyLaneState(lane, span.state);
              const pulseOpen =
                !pipelineComplete &&
                spans.length > 0 &&
                i === spans.length - 1 &&
                span.endMs === wallNowMs;
              const tip = pulseOpen
                ? `${readable}: about ${durationLabel} so far (still running)`
                : `${readable}: ${durationLabel} (${formatTime(new Date(span.startMs).toISOString())} – ${formatTime(new Date(span.endMs).toISOString())})`;
              const segmentText = pulseOpen
                ? `${readable} · ${durationLabel} (running)`
                : `${readable} · ${durationLabel}`;
              const w = Math.max(0.35, seg.widthFrac * 100);
              const showTxt = seg.widthFrac >= 0.07;
              return (
                <Box
                  key={`${rowKey}-${lane}-${span.state}-${span.startMs}-${i}`}
                  component="span"
                  title={tip}
                  className={clsx(classes.timelineSegment, {
                    [classes.timelineSegmentLive]: pulseOpen,
                  })}
                  style={{
                    left: `${seg.leftFrac * 100}%`,
                    width: `${w}%`,
                    backgroundColor: bg,
                    borderRight: `1px solid ${theme.palette.divider}`,
                  }}
                >
                  {showTxt && (
                    <Box
                      component="span"
                      className={classes.timelineSegmentLabel}
                      style={{ color: fg }}
                    >
                      {segmentText}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box className={classes.timelineUnifiedStack}>
      {renderLaneRow('lane-batch', 'Batch', batchSpans, 'batch')}
      {files.map((f, fi) =>
        renderLaneRow(
          `lane-file-${f.id}`,
          f.fileName,
          fileSpanGroups[fi] ?? [],
          'file',
        ),
      )}
      <Box className={classes.timelineUnifiedRow}>
        <Box aria-hidden className={classes.timelineCaptionSpacerCell} />
        <Box className={classes.timelineRowTrackWrap}>
          <Box className={classes.timelineCaption}>
            <span>Start · {t0}</span>
            <span>End · {t1}</span>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

/** Toggle + pre for each OpenAI `json_schema` root (readable vs JSON), like admin / AI Chat Tools. */
type BatchAiStructuredSchemaViewMode = 'readable' | 'json';

const BatchAiPromptStructuredOutputSlice: React.FC<{
  sectionId: string;
  ix: number;
  item: ProjectFileBatchAiChainStructuredOutput;
}> = ({ sectionId, ix, item }) => {
  const { classes, cx } = useStyles();
  const [view, setView] = useState<BatchAiStructuredSchemaViewMode>('readable');
  const text =
    view === 'readable'
      ? humanizeJsonSchema(item.schema)
      : JSON.stringify(item.schema, null, 2);

  const renderText = (val: string) => {
    if (view !== 'readable') return val;
    return val.split('\n').map((line, i) => {
      const parts = line.split(/(\*\*[^*]+\*\*)/);
      return (
        <React.Fragment key={i}>
          {parts.map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) {
              return <strong key={j}>{part.slice(2, -2)}</strong>;
            }
            return part;
          })}
          {i < val.split('\n').length - 1 && '\n'}
        </React.Fragment>
      );
    });
  };

  return (
    <Box
      className={cx(
        classes.batchAiPromptStructuredSlice,
        ix === 0 && classes.batchAiPromptStructuredSliceFirst,
      )}
    >
      <Typography variant="body2" className={classes.batchAiPromptSchemaLabel}>
        {item.label}
      </Typography>
      <Box className={classes.batchAiPromptStructuredSliceToolbar}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Label
            component="span"
            label={item.structuredOutputName}
            className={classes.batchAiPromptSchemaNameChip}
          />
          <Typography
            variant="caption"
            className={classes.batchAiPromptSchemaLabel}
          >
            {item.label}
          </Typography>
        </Box>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={view}
          onChange={(_, v: BatchAiStructuredSchemaViewMode | null) =>
            v && setView(v)
          }
          aria-label={`Schema view for ${sectionId}-${item.id}`}
        >
          <ToggleButton value="readable" sx={{ px: 1.5, py: 0.5 }}>
            Readable
          </ToggleButton>
          <ToggleButton value="json" sx={{ px: 1.5, py: 0.5 }}>
            JSON
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>
      <Typography
        component="pre"
        className={classes.batchAiPromptSchemaMonoPre}
      >
        {renderText(text)}
      </Typography>
    </Box>
  );
};

const BatchAiPromptStructuredOutputs: React.FC<{
  sectionId: string;
  items: readonly ProjectFileBatchAiChainStructuredOutput[];
}> = ({ sectionId, items }) => {
  const { classes } = useStyles();
  if (!items.length) return null;
  return (
    <Box component="section" aria-labelledby={`${sectionId}-schema-heading`}>
      <Typography
        id={`${sectionId}-schema-heading`}
        variant="subtitle2"
        className={classes.batchAiPromptStructuredHeading}
      >
        Output schema (Responses API json_schema)
      </Typography>
      {items.map((item, ix) => (
        <BatchAiPromptStructuredOutputSlice
          key={`${sectionId}-${item.id}`}
          sectionId={sectionId}
          ix={ix}
          item={item}
        />
      ))}
    </Box>
  );
};

type SelectedBatchDetailProps = {
  snapshot: ProjectFileBatchSnapshot;
  uploaderLabel: string | null;
};

/**
 * Selected batch view. A throw here is caught by {@link BatchViewErrorBoundary}
 * so the batch list stays usable.
 */
const SelectedBatchDetail: React.FC<SelectedBatchDetailProps> = ({
  snapshot,
  uploaderLabel,
}) => {
  const { classes, cx } = useStyles();
  const [drawingsModalFileId, setDrawingsModalFileId] = useState<string | null>(
    null,
  );

  /**
   * Stable client-side ordering by (createdAt, id). The server already orders
   * by created_at + id but defensive sort here covers any optimistic patches
   * that may have arrived in a different order.
   */
  const sortedFiles = useMemo(() => {
    return [...snapshot.files].sort((a, b) => {
      if (a.createdAt < b.createdAt) return -1;
      if (a.createdAt > b.createdAt) return 1;
      return a.id.localeCompare(b.id);
    });
  }, [snapshot]);

  const classifyingDurationByFileId = useMemo(() => {
    const durations = new Map<string, number>();
    for (const file of snapshot.files) {
      const ms = getProjectFilePhaseDurationMs(
        snapshot.batch.timeline,
        file.id,
        'classifying',
      );
      if (ms != null) durations.set(file.id, ms);
    }
    return durations;
  }, [snapshot]);

  const drawingsModalFile =
    sortedFiles.find((file) => file.id === drawingsModalFileId) ?? null;
  const aiCostSummary = useMemo(
    () =>
      formatAiCostSummary(summarizeProjectFileAiCost(snapshot.batch.aiCalls)),
    [snapshot.batch.aiCalls],
  );

  return (
    <>
      <Box className={classes.detailContent}>
        <Box className={classes.meta}>
          <Label
            label={friendlyBatchPhase(snapshot.batch.state)}
            color={STATE_COLOR[snapshot.batch.state]}
          />
          <span>Created {formatTime(snapshot.batch.createdAt)}</span>
          <span>Last updated {formatTime(snapshot.batch.updatedAt)}</span>
          <span>Uploaded by {visibleUploaderName(uploaderLabel)}</span>
          <span>Organization {snapshot.batch.organizationId}</span>
          {projectFilePipeLabel(snapshot.batch.pipeId) != null && (
            <span>Pipe {projectFilePipeLabel(snapshot.batch.pipeId)}</span>
          )}
          {aiCostSummary != null && <span>{aiCostSummary}</span>}
          <BatchProjectLink batch={snapshot.batch} />
        </Box>
        <AiStepCostTable calls={snapshot.batch.aiCalls} />
        {snapshot.batch.failureReason && (
          <Box className={classes.section}>
            <Typography
              variant="subtitle2"
              color="error"
              className={classes.extractionErrorLine}
            >
              Something went wrong
            </Typography>
            <Typography
              variant="body2"
              color="error"
              className={classes.extractionErrorLine}
              component="pre"
            >
              {snapshot.batch.failureReason}
            </Typography>
          </Box>
        )}
        {showConsolidatedOutputSection(snapshot.batch) && (
          <Box className={classes.section}>
            {snapshot.batch.aggregatedOutput != null ? (
              <BatchAggregatedOutputView
                value={snapshot.batch.aggregatedOutput}
                finalOutput={snapshot.batch.finalOutput}
                plans={{
                  batchId: snapshot.batch.id,
                  groups: planOverlayGroups(sortedFiles),
                }}
              />
            ) : (
              <Box
                className={classes.consolidatedOutputSkeletonRoot}
                role="status"
                aria-label="Generating summary"
              >
                <Skeleton variant="text" width="42%" height={22} />
                <Skeleton variant="text" width="78%" height={20} />
                <Skeleton variant="rounded" height={72} />
                <Skeleton variant="text" width="56%" height={22} />
                <Skeleton variant="rounded" height={140} />
              </Box>
            )}
          </Box>
        )}
        {snapshot.batch.aggregatedOutput == null && (
          <PlanOverlayStack batchId={snapshot.batch.id} files={sortedFiles} />
        )}
        {snapshot.batch.timeline.length > 0 && (
          <Box className={classes.section}>
            <Typography variant="subtitle2">
              Progress · {snapshot.batch.timeline.length}{' '}
              {snapshot.batch.timeline.length === 1 ? 'step' : 'steps'}
            </Typography>
            <UnifiedPipelineTimelines
              classes={{
                timelineUnifiedStack: classes.timelineUnifiedStack,
                timelineUnifiedRow: classes.timelineUnifiedRow,
                timelineRowLabelCol: classes.timelineRowLabelCol,
                timelineRowTrackWrap: classes.timelineRowTrackWrap,
                timelineLaneStacked: classes.timelineLaneStacked,
                timelineTrack: classes.timelineTrack,
                timelineSegment: classes.timelineSegment,
                timelineSegmentLive: classes.timelineSegmentLive,
                timelineCaption: classes.timelineCaption,
                timelineCaptionSpacerCell: classes.timelineCaptionSpacerCell,
                timelineSegmentLabel: classes.timelineSegmentLabel,
              }}
              timeline={snapshot.batch.timeline}
              batchState={snapshot.batch.state}
              batchCreatedAt={snapshot.batch.createdAt}
              files={sortedFiles}
            />
            <details className={classes.timelineRawWrap}>
              <summary className={classes.timelineRawSummary}>
                Full event log
              </summary>
              <pre className={classes.pre}>
                {snapshot.batch.timeline
                  .map((e) => {
                    const t = new Date(e.at).toISOString().slice(11, 23);
                    const fileSuffix =
                      e.scope === 'file' ? ` (${e.fileId.slice(0, 8)})` : '';
                    return `${t}  ${e.scope}.${e.state}.${e.event}${fileSuffix}`;
                  })
                  .join('\n')}
              </pre>
            </details>
          </Box>
        )}
        <Box className={classes.section}>
          <Typography variant="subtitle2">
            Files ({sortedFiles.length})
          </Typography>
          <Box className={classes.batchFilesFlex}>
            {sortedFiles.map((f) => {
              const classifyingMs = classifyingDurationByFileId.get(f.id);
              return (
                <Box key={f.id} className={classes.batchFileTile}>
                  <Box className={classes.batchFileHeaderRow}>
                    <Box className={classes.batchFilePrimary}>
                      <Label
                        label={friendlyFilePhase(f.state)}
                        color={f.state === 'failed' ? 'error' : undefined}
                      />
                      <Typography variant="body2" component="span">
                        {f.fileName}
                      </Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      className={classes.batchFileMetaCaption}
                      sx={{
                        color: 'text.secondary',
                      }}
                    >
                      {f.mimeType}
                      {f.sizeBytes !== null &&
                        ` · ${formatFileSizeMegabytes(f.sizeBytes)}`}
                      {classifyingMs !== undefined && (
                        <>
                          {' · '}
                          classifying took{' '}
                          {formatPhaseDurationMs(classifyingMs)}
                        </>
                      )}
                    </Typography>
                  </Box>
                  {f.failureReason != null && f.failureReason.trim() !== '' && (
                    <Typography
                      variant="body2"
                      color="error"
                      component="pre"
                      className={classes.extractionErrorLine}
                    >
                      {f.failureReason}
                    </Typography>
                  )}
                  {documentKindSkeleton(f) && (
                    <Box className={classes.filePendingDocumentKindRow}>
                      <Typography
                        variant="caption"
                        component="span"
                        sx={{
                          color: 'text.secondary',
                        }}
                      >
                        Document type
                      </Typography>
                      <Skeleton variant="rounded" width={148} height={28} />
                    </Box>
                  )}
                  {fileClassificationLabelsAvailable(f.state) &&
                    f.classifierDocumentKind != null && (
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.75,
                          mt: 0.25,
                          flexWrap: 'wrap',
                        }}
                      >
                        <Typography
                          variant="caption"
                          component="span"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          Document type
                        </Typography>
                        <Tooltip
                          title={
                            getDocumentKindTooltip(f.classifierDocumentKind) ??
                            ''
                          }
                        >
                          <Label
                            label={formatDocumentKindKey(
                              f.classifierDocumentKind,
                            )}
                          />
                        </Tooltip>
                        {f.classifierDocumentKindConfidence != null && (
                          <Typography
                            variant="caption"
                            component="span"
                            sx={{
                              color: 'text.disabled',
                            }}
                          >
                            {Math.round(
                              f.classifierDocumentKindConfidence * 100,
                            )}
                            %
                          </Typography>
                        )}
                      </Box>
                    )}
                  {f.extractedDrawings.length > 0 && (
                    <Box className={classes.fileDrawingsButton}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => setDrawingsModalFileId(f.id)}
                      >
                        Drawings ({f.extractedDrawings.length})
                      </Button>
                    </Box>
                  )}
                  {classificationsSkeleton(f) && (
                    <Box className={classes.filePendingBlock}>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          display: 'block',
                        }}
                      >
                        Findings
                      </Typography>
                      <Box className={classes.fileClassificationSkeletonCards}>
                        <Skeleton variant="rounded" width={260} height={96} />
                        <Skeleton variant="rounded" width={260} height={96} />
                      </Box>
                    </Box>
                  )}
                  {extractionSkeleton(f) ? (
                    <FileSegmentPairs
                      classes={classes}
                      cx={cx}
                      file={f}
                      extractionPending
                    />
                  ) : (
                    (f.classifications.length > 0 ||
                      f.extractionOutput != null) && (
                      <details className={classes.timelineRawWrap}>
                        <summary className={classes.timelineRawSummary}>
                          Per fil
                        </summary>
                        <FileSegmentPairs
                          classes={classes}
                          cx={cx}
                          file={f}
                          extractionPending={false}
                        />
                      </details>
                    )
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
      {drawingsModalFile != null && (
        <ExtractedDrawingsDialog
          open
          onClose={() => setDrawingsModalFileId(null)}
          batchId={snapshot.batch.id}
          fileId={drawingsModalFile.id}
          fileName={drawingsModalFile.fileName}
          drawings={drawingsModalFile.extractedDrawings}
          pageRasters={drawingsModalFile.pageRasters ?? []}
        />
      )}
    </>
  );
};

/**
 * The linked project, or (FFU baseline POC, admin only) a button that creates
 * one from the template and the batch's final output.
 */
const BatchProjectLink: React.FC<{
  batch: ProjectFileBatchSnapshot['batch'];
}> = ({ batch }) => {
  const [projectId, setProjectId] = useState<number | null>(batch.projectId);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [changes, setChanges] = useState<string | null>(null);
  useEffect(() => setProjectId(batch.projectId), [batch.projectId]);
  if (projectId !== null) {
    return (
      <>
        <a href={`/${batch.organizationId}/projects/${projectId}`}>
          Linked project {projectId}
        </a>
        {changes !== null && <span>Changed in template: {changes}</span>}
      </>
    );
  }
  if (batch.state !== 'done' || batch.finalOutput == null) return null;
  const create = () => {
    setCreating(true);
    setError(null);
    adminProjectFileBatchesApi
      .createProject(batch.id)
      .then((result) => {
        setChanges(
          result.changes.length === 0
            ? 'nothing'
            : result.changes
                .map((change) => `${change.property} = ${change.value}`)
                .join(', '),
        );
        setProjectId(result.projectId);
      })
      .catch((reason: unknown) => setError(String(reason)))
      .finally(() => setCreating(false));
  };
  return (
    <>
      <Button
        size="small"
        variant="outlined"
        disabled={creating}
        onClick={create}
      >
        {creating ? 'Creating project…' : 'Create project'}
      </Button>
      {error !== null && <span>{error}</span>}
    </>
  );
};

export const AdminProjectFileBatches: React.FC<{
  view?: 'batches' | 'ai-pipeline';
}> = ({ view: viewProp }) => {
  const { classes, cx } = useStyles();
  const user = useUser();
  const { getAccessTokenSilently } = useAuth0();
  const [config] = useConfig();
  const selectedOrganization = useSelectedOrganizationName();
  const socketRef = useRef<Socket | undefined>(undefined);

  const [rows, setRows] = useState<ProjectFileBatchAdminRow[]>([]);
  const [batchParam, setBatchParam] = useSearchParam('batch');
  const selectedId = batchParam === '' ? null : batchParam;
  const [snapshot, setSnapshot] = useState<ProjectFileBatchSnapshot | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [pipeId, setPipeId] = useState<ProjectFilePipeId>(FFU_PIPE_ID);
  const [model, setModel] = useState('');
  const [view, setView] = useState<'batches' | 'ai-pipeline'>(
    viewProp || 'batches',
  );
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const folderInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (viewProp) setView(viewProp);
  }, [viewProp]);

  const callerIsAdmin = useMemo(() => isAdmin(user), [user]);

  const batchAiPromptChainSections = useMemo(
    () => getProjectFileBatchAiChainPromptSections(),
    [],
  );

  const selectedBatchUploaderLabel = useMemo(() => {
    if (snapshot == null) return null;
    const fromSnap = snapshot.batchUserDisplayName?.trim();
    if (fromSnap) return fromSnap;
    return (
      rows.find((r) => r.id === snapshot.batch.id)?.userDisplayName?.trim() ??
      null
    );
  }, [snapshot, rows]);

  const fetchList = useCallback(async () => {
    const res = await tryCatchAwaitable(adminProjectFileBatchesApi.list());
    if (!res.ok) {
      setError(getErrorResponseMessage(res.error));
      return;
    }
    setRows(res.value);
    setError(null);
  }, []);

  const fetchSnapshot = useCallback(async (id: string) => {
    const res = await tryCatchAwaitable(adminProjectFileBatchesApi.get(id));
    if (!res.ok) {
      setError(getErrorResponseMessage(res.error));
      return;
    }
    setSnapshot(res.value);
  }, []);

  const uploadFiles = useCallback(
    async (uploads: ProjectFileClientUpload[]) => {
      if (uploads.length === 0 || uploading) return;
      setUploading(true);
      const res = await tryCatchAwaitable(
        adminProjectFileBatchesApi.create(uploads, {
          pipeId,
          model:
            isSingleCallProjectFilePipe(pipeId) && model !== '' ? model : null,
        }),
      );
      setUploading(false);
      if (!res.ok) {
        setError(getErrorResponseMessage(res.error));
        return;
      }
      setError(null);
      // Open the new batch immediately so the user sees it tick through
      // states. The list update arrives via WS within the same render cycle.
      setBatchParam(res.value.batch.id);
      void fetchList();
    },
    [uploading, fetchList, setBatchParam, pipeId, model],
  );

  useEffect(() => {
    if (!callerIsAdmin) return;
    void fetchList();
  }, [callerIsAdmin, fetchList]);

  useEffect(() => {
    if (!selectedId) {
      setSnapshot(null);
      return;
    }
    void fetchSnapshot(selectedId);
  }, [selectedId, fetchSnapshot]);

  // WS subscription. Admin sockets receive every batch update across orgs.
  // Splice the row in the list and refresh the open detail when it matches.
  useEffect(() => {
    if (!callerIsAdmin || !selectedOrganization) return;

    let cancelled = false;
    let socket: Socket | undefined;

    const connect = async () => {
      const token = await getAccessTokenSilently();
      if (cancelled) return;
      const sessionId = window.sessionStorage.getItem('id') ?? uuid.v4();

      socket = io(config.apiUrl, {
        auth: { token: `Bearer ${token}` },
        extraHeaders: { AppVersion: config.version },
        query: { organization: selectedOrganization, sessionId },
        transports: ['websocket'],
      });
      socketRef.current = socket;

      socket.on(
        SocketEventName.ProjectFileBatchUpdate,
        (data: ProjectFileBatchSnapshot) => {
          setRows((prev) => {
            const labels = getSuggestedProjectLabelsFromProjectFiles(
              data.files,
            );
            const idx = prev.findIndex((r) => r.id === data.batch.id);
            const existing = idx !== -1 ? prev[idx] : undefined;
            const { batch } = data;
            const updated: ProjectFileBatchAdminRow = {
              id: batch.id,
              userId: batch.userId,
              organizationId: batch.organizationId,
              projectId: batch.projectId,
              state: batch.state,
              pipeId: batch.pipeId,
              model: batch.model,
              failureReason: batch.failureReason,
              createdAt: batch.createdAt,
              updatedAt: batch.updatedAt,
              aiCost: summarizeProjectFileAiCost(batch.aiCalls),
              fileCounts: {
                total: data.files.length,
                done: data.files.filter((f) => f.state === 'done').length,
                failed: data.files.filter((f) => f.state === 'failed').length,
              },
              suggestedProjectName: labels.suggestedProjectName,
              suggestedProjectDescription: labels.suggestedProjectDescription,
              userDisplayName: existing?.userDisplayName ?? null,
            };
            if (idx === -1) return [updated, ...prev];
            const next = [...prev];
            next[idx] = updated;
            return next;
          });
          setSnapshot((prev) => {
            if (!prev || prev.batch.id !== data.batch.id) return prev;
            return {
              ...data,
              batchUserDisplayName:
                prev.batchUserDisplayName ?? data.batchUserDisplayName,
            };
          });
        },
      );
    };

    void connect();

    return () => {
      cancelled = true;
      socket?.removeAllListeners();
      socket?.disconnect();
      socketRef.current = undefined;
    };
  }, [callerIsAdmin, selectedOrganization, getAccessTokenSilently, config]);

  if (!callerIsAdmin) {
    return (
      <Box
        sx={{
          p: 3,
        }}
      >
        <Typography color="error">
          This page is only available to administrators.
        </Typography>
      </Box>
    );
  }

  const showCatalog = view === 'ai-pipeline' || !snapshot;

  return (
    <Box className={classes.root}>
      <Box className={classes.mainRow}>
        {view === 'batches' && (
          <Box className={classes.list}>
            <FormControl fullWidth size="small">
              <InputLabel id="project-file-pipe-label">Pipe</InputLabel>
              <Select
                labelId="project-file-pipe-label"
                label="Pipe"
                value={pipeId}
                onChange={(event) => {
                  const next = event.target.value;
                  if (!isProjectFilePipeId(next)) return;
                  setPipeId(next);
                }}
              >
                {PROJECT_FILE_PIPES.map((pipe) => (
                  <MenuItem key={pipe.id} value={pipe.id}>
                    {pipe.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {pipeId === FFU_SINGLE_CALL_PIPE_ID && (
              <FormControl fullWidth size="small">
                <InputLabel id="project-file-pipe-model-label">
                  Model
                </InputLabel>
                <Select
                  labelId="project-file-pipe-model-label"
                  label="Model"
                  value={model}
                  onChange={(event) => setModel(event.target.value)}
                >
                  <MenuItem value="">Consolidation default</MenuItem>
                  {OPEN_AI_ALLOWED_MODELS.map((allowed) => (
                    <MenuItem key={allowed} value={allowed}>
                      {allowed}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Box
              className={cx(classes.dropZone, {
                [classes.dropZoneActive]: dragging,
              })}
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                if (!dragging) setDragging(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setDragging(false);
              }}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                void collectProjectFilesFromDataTransfer(e.dataTransfer, {
                  keepAllSourceFiles: isSingleCallProjectFilePipe(pipeId),
                }).then((uploads) => {
                  if (uploads.length === 0) {
                    setError('No usable files in the drop');
                    return;
                  }
                  void uploadFiles(uploads);
                });
              }}
            >
              {error && (
                <Typography variant="caption" color="error">
                  {error}
                </Typography>
              )}
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => {
                  const uploads = collectProjectFilesFromFileList(
                    e.target.files ?? [],
                    { keepAllSourceFiles: isSingleCallProjectFilePipe(pipeId) },
                  );
                  if (e.target) e.target.value = '';
                  void uploadFiles(uploads);
                }}
              />
              <input
                ref={(el) => {
                  folderInputRef.current = el;
                  if (!el) return;
                  el.setAttribute('webkitdirectory', '');
                  el.setAttribute('directory', '');
                }}
                type="file"
                hidden
                onChange={(e) => {
                  const uploads = collectProjectFilesFromFileList(
                    e.target.files ?? [],
                    { keepAllSourceFiles: isSingleCallProjectFilePipe(pipeId) },
                  );
                  if (e.target) e.target.value = '';
                  void uploadFiles(uploads);
                }}
              />
              <Typography variant="caption" color="text.secondary">
                {uploading ? 'Uploading…' : 'Drop files'}
              </Typography>
              <Button size="small" variant="text" disabled={uploading}>
                Files
              </Button>
              <Button
                size="small"
                variant="text"
                disabled={uploading}
                onClick={(e) => {
                  e.stopPropagation();
                  folderInputRef.current?.click();
                }}
              >
                Folder
              </Button>
            </Box>

            {rows.length === 0 && (
              <Box
                sx={{
                  p: 2,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                  }}
                >
                  No uploads in the list yet.
                </Typography>
              </Box>
            )}
            {rows.map((r) => {
              const name = r.suggestedProjectName?.trim() || 'Namnlös';
              const phase =
                r.state === 'done' ? null : friendlyBatchPhase(r.state);
              const pipeLabel = projectFilePipeLabel(r.pipeId);
              const aiCost = formatAiCostUsd(r.aiCost);
              return (
                <Box
                  key={r.id}
                  className={cx(classes.row, {
                    [classes.rowActive]: r.id === selectedId,
                  })}
                  onClick={() => setBatchParam(r.id)}
                >
                  <p className={classes.rowName} title={name}>
                    {name}
                  </p>
                  <p className={classes.rowMeta}>
                    {formatTime(r.updatedAt)}
                    {' · '}
                    {r.fileCounts.done}/{r.fileCounts.total}
                    {pipeLabel != null ? ` · ${pipeLabel}` : ''}
                    {aiCost != null ? ` · ${aiCost}` : ''}
                    {phase != null ? ` · ${phase}` : ''}
                    {r.fileCounts.failed > 0
                      ? ` · ${r.fileCounts.failed} fel`
                      : ''}
                  </p>
                  {r.state === 'failed' &&
                    r.failureReason != null &&
                    r.failureReason.trim() !== '' && (
                      <p className={classes.rowMeta} title={r.failureReason}>
                        {r.failureReason}
                      </p>
                    )}
                </Box>
              );
            })}
          </Box>
        )}

        <Box className={classes.detail}>
          {showCatalog ? (
            view === 'batches' ? (
              <Box className={classes.emptyState}>
                <InboxOutlinedIcon className={classes.emptyStateIcon} />
                <Typography
                  variant="body1"
                  sx={{
                    color: 'text.primary',
                  }}
                >
                  No batch selected
                </Typography>
                <Typography variant="body2">
                  Select a batch from the list to see its details, or drop files
                  in the upload area to start a new one.
                </Typography>
              </Box>
            ) : (
              <Box className={classes.detailIdlePromptLayout}>
                <Typography
                  variant="h4"
                  className={classes.batchAiPromptIntro}
                  gutterBottom
                >
                  Batch-file AI pipeline — prompt reference
                </Typography>
                <Typography
                  variant="body2"
                  className={classes.batchAiPromptCaption}
                  sx={{ mb: 3 }}
                >
                  This reference shows the exact prompts and output schemas used
                  in each stage of the AI analysis pipeline. Dynamic parts use
                  placeholders like{' '}
                  <span
                    className={classes.batchAiPromptMono}
                  >{`{{originalFilename}}`}</span>
                  ,{' '}
                  <span
                    className={classes.batchAiPromptMono}
                  >{`{{projectLabel}}`}</span>
                  , or{' '}
                  <span className={classes.batchAiPromptMono}>
                    {`{{batchMergePayloadJson}}`}
                  </span>
                  . These templates are shared between the client and server to
                  ensure consistency.
                </Typography>
                <Box className={classes.batchAiPromptChainScroll}>
                  {batchAiPromptChainSections.map((s) => (
                    <Card
                      key={s.id}
                      variant="outlined"
                      className={classes.batchAiPromptCard}
                    >
                      <CardContent className={classes.batchAiPromptCardContent}>
                        <Typography
                          variant="caption"
                          component="div"
                          className={classes.batchAiPromptPhase}
                        >
                          {s.phaseLabel}
                        </Typography>
                        <Typography
                          variant="h5"
                          component="h3"
                          gutterBottom
                          sx={{ fontWeight: 'bold' }}
                        >
                          {s.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          className={classes.batchAiPromptPurpose}
                          sx={{ mb: 1 }}
                        >
                          {s.purpose}
                        </Typography>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            className={classes.batchAiPromptContextHeading}
                          >
                            Data sent with the request
                          </Typography>
                          <Box
                            component="ul"
                            className={classes.batchAiPromptContextList}
                          >
                            {s.contextSent.map((line, i) => (
                              <Typography
                                key={`${s.id}-ctx-${i}`}
                                component="li"
                                variant="body2"
                                className={classes.batchAiPromptContextLi}
                                sx={{
                                  color: 'text.secondary',
                                }}
                              >
                                {line}
                              </Typography>
                            ))}
                          </Box>
                        </Box>
                        <Box>
                          <Typography
                            variant="subtitle2"
                            className={classes.batchAiPromptBodyHeading}
                          >
                            Instruction text (placeholders like{' '}
                            <span
                              className={classes.batchAiPromptMono}
                            >{`{{originalFilename}}`}</span>{' '}
                            are filled at runtime)
                          </Typography>
                          <Typography component="pre" className={classes.pre}>
                            {s.body}
                          </Typography>
                        </Box>
                        <Divider />
                        <BatchAiPromptStructuredOutputs
                          sectionId={s.id}
                          items={s.structuredOutputs}
                        />
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              </Box>
            )
          ) : (
            <BatchViewErrorBoundary
              resetKey={snapshot.batch.id}
              className={classes.emptyState}
            >
              <SelectedBatchDetail
                snapshot={snapshot}
                uploaderLabel={selectedBatchUploaderLabel}
              />
            </BatchViewErrorBoundary>
          )}
        </Box>
      </Box>
    </Box>
  );
};
